/**
 * Translation strings, keyed by a stable identifier.
 *
 * Keep the keys descriptive (e.g. `dashboard.balanceOverview.title`) so the
 * key alone documents the location. Both `id` and `en` blocks must have the
 * same set of keys — TypeScript enforces this via the `TranslationKey` union.
 *
 * Variable interpolation:
 *   "Welcome, {name}" + t('dashboard.welcome', { name: 'Alice' })
 *   → "Welcome, Alice"
 */

const id = {
  // ===== common =====
  'common.save': 'Simpan',
  'common.cancel': 'Batal',
  'common.close': 'Tutup',
  'common.reset': 'Reset',
  'table.resetWidths': 'Reset lebar kolom',
  'table.resizeColumn': 'Ubah lebar kolom (seret, atau panah kiri/kanan; Home untuk reset)',
  'table.columns': 'Kolom',
  'table.showAll': 'Tampilkan semua',
  'table.scrollLeft': 'Geser tabel ke kiri',
  'table.scrollRight': 'Geser tabel ke kanan',
  'pagination.summary': '{total} baris · halaman {page} dari {pages}',
  'pagination.perPage': 'per halaman',
  'listSummary.title': 'Ringkasan data terfilter',
  'listSummary.filteredHint': 'Mengikuti filter yang sedang diterapkan (semua status)',
  'listSummary.loadFailed': 'Gagal memuat ringkasan',
  'listSummary.total': 'Total transaksi',
  'listSummary.amount': 'Total amount',
  'listSummary.fee': 'Total fee',
  'listSummary.revenue': 'Total merchant revenue',
  'listSummary.byStatus': 'Per status',
  'listSummary.byMethod': 'Per metode',
  'listSummary.byBank': 'Per bank',
  'listSummary.count': 'trx',
  'listSummary.groups': '{n} grup',
  'listSummary.showAll': 'Tampilkan semua ({n})',
  'listSummary.showTop': 'Tampilkan {n} teratas',
  'list.toggleOverview': 'Tampilkan / sembunyikan ringkasan data terfilter',
  'list.toggleFilters': 'Tampilkan / sembunyikan filter',
  'json.copy': 'Salin JSON',
  'json.copied': 'Tersalin',
  'json.viewCode': 'JSON',
  'json.viewTable': 'Tabel',
  'json.empty': 'kosong',
  'json.items': '{n} item',
  'json.keys': '{n} key',
  'common.refresh': 'Refresh',
  'common.reload': 'Reload',
  'common.filters': 'Filter',
  'common.apply': 'Terapkan',
  'common.delete': 'Hapus',
  'common.create': 'Buat',
  'common.add': 'Tambah',
  'common.back': 'Kembali',
  'common.error': 'Error',
  'common.unknown': 'Tidak diketahui',
  'common.collapse': 'Tutup',
  'common.show': 'Tampilkan',
  'common.next': 'Selanjutnya',
  'common.previous': 'Sebelumnya',
  'common.prev': 'Sebelumnya',

  // ===== auth =====
  'auth.superuser': 'Superuser',
  'auth.internal': 'Internal',
  'auth.merchant': 'Merchant',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.captcha': 'Captcha',
  'auth.logout': 'Logout',
  'auth.loginTitle': 'TransPay Dashboard',
  'auth.confirmPassword': 'Konfirmasi password Anda',
  'auth.unlock': 'Buka',
  'auth.loginTagline': 'Masuk untuk melihat saldo, transaksi, dan kelola merchant.',
  'auth.emailPlaceholder': 'user_merchant@trans-pay.app',
  'auth.passwordPlaceholder': '••••••••',
  'auth.captchaPlaceholder': 'Ketik kode di kanan',
  'auth.captchaCurrent': 'Kode saat ini: {code}',
  'auth.refreshCaptcha': 'Refresh captcha',
  'auth.signIn': 'Sign In',
  'auth.needAccess': 'Memerlukan akses?',
  'auth.contactAdmin': 'Hubungi admin Anda',
  'auth.errInvalidEmail': 'Email tidak valid',
  'auth.errPasswordTooShort': 'Min 8 karakter',
  'auth.errIpWhitelistNotConfigured':
    'Akun belum diberi IP whitelist. Hubungi admin untuk menambahkan IP Anda.',
  'auth.errIpNotWhitelisted':
    'IP Anda tidak terdaftar di whitelist. Login dari lokasi yang diizinkan, atau minta admin menambahkan IP.',
  'auth.errInvalidCredentials': 'Email atau password salah.',
  'auth.errCaptchaMismatch': 'Kode captcha tidak cocok.',
  'auth.errConcurrentLogin':
    'Terdeteksi sesi aktif lain untuk akun ini. Semua sesi telah diakhiri demi keamanan — silakan login ulang.',
  'auth.errMfaInvalid': 'Kode MFA salah atau kedaluwarsa.',
  'auth.errAccountLocked': 'Terlalu banyak percobaan gagal. Akun dikunci sementara, coba lagi nanti.',
  // --- Suspend / login-counter messages (migration 0014) ---
  'auth.errInvalidCredentialsWithCount': 'Email atau password salah. Percobaan {count} dari {max}.',
  'auth.errIpNotWhitelistedWithCount':
    'IP {ip} tidak terdaftar di whitelist Anda. Percobaan {count} dari {max}.',
  'auth.errMfaInvalidWithCount': 'Kode MFA salah atau kedaluwarsa. Percobaan {count} dari {max}.',
  'auth.errAccountSuspendedNow':
    'Akun Anda di-suspend karena {max} kali percobaan login gagal. Hubungi administrator untuk mengaktifkan kembali.',
  'auth.errAccountSuspended':
    'Akun Anda di-suspend. Hubungi administrator untuk mengaktifkan kembali.',
  'auth.errMerchantSuspendedNow':
    'Karena Anda merchant admin, merchant Anda beserta seluruh user di bawahnya juga ikut di-suspend.',
  'auth.errMerchantSuspended':
    'Merchant Anda di-suspend. Hubungi administrator untuk mengaktifkan kembali.',
  'auth.mfaCodeLabel': 'Kode MFA',
  'auth.mfaModalTitle': 'Verifikasi Dua Langkah (2FA)',
  'auth.mfaModalIntro':
    'Email & password Anda sudah benar. Akun ini dilindungi MFA — buka aplikasi authenticator (mis. Google Authenticator / Authy) di ponsel Anda dan masukkan 6 digit kode untuk melanjutkan.',
  'auth.mfaModalVerify': 'Verifikasi & Masuk',
  'auth.sessionExpiredTitle': 'Sesi berakhir',
  'auth.sessionExpired': 'Sesi Anda telah berakhir. Silakan masuk kembali.',
  'auth.sessionKilled.superseded.title': 'Anda masuk di perangkat lain',
  'auth.sessionKilled.superseded.body':
    'Sesi ini diakhiri karena akun Anda masuk di browser atau perangkat lain. Silakan masuk kembali.',
  'auth.sessionKilled.ipChanged.title': 'Alamat jaringan berubah',
  'auth.sessionKilled.ipChanged.body':
    'Alamat IP Anda berubah, sehingga demi keamanan sesi ini diakhiri. Silakan masuk kembali.',
  'auth.sessionKilled.concurrentTab.title': 'TransPay dibuka di tab lain',
  'auth.sessionKilled.concurrentTab.body':
    'TransPay dibuka di tab atau jendela lain. Demi keamanan, sesi ini diakhiri. Silakan masuk kembali.',
  'auth.sessionKilled.loggedOut.title': 'Sesi diakhiri',
  'auth.sessionKilled.loggedOut.body': 'Sesi Anda telah diakhiri. Silakan masuk kembali.',
  'auth.sessionKilled.suspended.title': 'Akun ditangguhkan',
  'auth.sessionKilled.suspended.body': 'Akun Anda telah ditangguhkan. Hubungi administrator Anda.',
  'auth.sessionKilled.button': 'Masuk kembali',

  // ===== nav =====
  'nav.overview': 'Overview',
  'nav.deposits': 'Deposit',
  'nav.disbursements': 'Disbursement',
  'nav.merchants': 'Merchant',
  'nav.wallet': 'Wallet',
  'nav.apiCredentials': 'Kredensial API',
  'nav.users': 'User',
  'nav.roles': 'Role & Permission',
  'nav.apiDocs': 'Dokumentasi API',
  'nav.security': 'Security',
  'nav.settings': 'Platform Settings',
  'nav.acquirers': 'Acquirer',
  'nav.auditLog': 'Audit Log',
  'nav.transactionReports': 'Laporan Transaksi',
  'nav.transactionSummary': 'Ringkasan Transaksi',
  'nav.settlementReports': 'Laporan Settlement',

  // ===== summary — ringkasan agregat transaksi on-screen (gaya Lunahub) =====
  'summary.title': 'Ringkasan Transaksi',
  'summary.subtitle': 'Ringkasan agregat transaksi per metode & bank untuk rentang tanggal',
  'summary.dateRange': 'Rentang tanggal',
  'summary.kpiCashIn': 'Total Cash In',
  'summary.kpiCashOut': 'Total Cash Out',
  'summary.kpiFee': 'Total Fee',
  'summary.kpiFeeHint': 'deposit + disbursement',
  'summary.kpiTrxPaid': '{count} trx paid',
  'summary.cashInDetail': 'Detail Cash In',
  'summary.cashOutDetail': 'Detail Cash Out',
  'summary.colProduct': 'Produk / Metode',
  'summary.colTrxCount': 'Jumlah Trx',
  'summary.colGross': 'Pendapatan Kotor',
  'summary.colFee': 'Fee',
  'summary.searchPlaceholder': 'Cari…',
  'summary.empty': 'Tidak ada transaksi pada rentang ini',
  'summary.presetToday': 'Hari Ini',
  'summary.presetYesterday': 'Kemarin',
  'summary.presetLast7': '7 Hari Terakhir',
  'summary.presetLast30': '30 Hari Terakhir',
  'summary.presetThisMonth': 'Bulan Ini',
  'summary.presetLastMonth': 'Bulan Lalu',
  'summary.presetThisYear': 'Tahun Ini',
  'summary.presetLastYear': 'Tahun Lalu',

  // ===== settlement — laporan settlement harian (pipeline sama dgn reports.*) =====
  'settlement.title': 'Laporan Settlement',
  'settlement.subtitleMerchant':
    'File laporan harian deposit merchant Anda yang sudah settle (Pending → Active), di-generate otomatis setiap pagi.',
  'settlement.subtitleInternal':
    'Laporan settlement harian per merchant. Filter merchant kosong = lihat semua merchant (memerlukan permission view.all).',
  'settlement.scheduleInfo':
    'Periode = tanggal SETTLEMENT (GMT+7), bukan tanggal transaksi: deposit masuk ke laporan pada hari dananya pindah dari Pending ke Active — baris Settlement yang sama dengan Balance Mutation di halaman Wallet. ' +
    'Deposit yang sudah dibayar tetapi masih menunggu jadwal settlement belum masuk. File di-generate otomatis setiap 04:00 WIB untuk settlement hari sebelumnya.',
  'settlement.colPeriod': 'Tanggal settlement',
  'settlement.colRows': 'Trx settled',
  'settlement.colNet': 'Net settled',
  'settlement.grossFeeHint': 'gross {gross} · fee {fee}',
  'settlement.empty': 'Belum ada laporan settlement untuk filter ini.',
  'settlement.noTransactions': 'Tidak ada settlement',
  'settlement.noTransactionsDetail':
    'Tidak ada deposit merchant ini yang settle pada tanggal tersebut, sehingga tidak ada file laporan yang dibuat (menghemat penyimpanan server).',
  'settlement.detailTitle': 'Detail Laporan Settlement — {period}',
  'settlement.fieldGross': 'Total gross',
  'settlement.fieldFee': 'Total fee',
  'settlement.fieldNet': 'Net settled',

  // ===== reports — transaction report (laporan transaksi harian) =====
  'reports.title': 'Laporan Transaksi',
  'reports.subtitleMerchant':
    'File laporan harian transaksi cashin + cashout merchant Anda, di-generate otomatis setiap pagi.',
  'reports.subtitleInternal':
    'Laporan harian per merchant. Filter merchant kosong = lihat semua merchant (memerlukan permission view.all).',
  'reports.scheduleInfo':
    'File di-generate otomatis setiap 04:00 WIB untuk transaksi hari sebelumnya. ' +
    'Setelah 4 hari sistem akan recheck data; jika ada perubahan, versi baru file akan tersedia.',
  'reports.fromDate': 'Dari tanggal',
  'reports.toDate': 'Sampai tanggal',
  'reports.merchantFilter': 'Merchant',
  'reports.merchantAll': 'Semua merchant',
  'reports.statusFilter': 'Status',
  'reports.statusAll': 'Semua status',
  'reports.colPeriod': 'Periode',
  'reports.colMerchant': 'Merchant',
  'reports.colStatus': 'Status',
  'reports.colVersion': 'Versi',
  'reports.colRows': 'Jumlah trx',
  'reports.colGenerated': 'Di-generate',
  'reports.colSize': 'Ukuran',
  'reports.colReason': 'Alasan',
  'reports.empty': 'Belum ada laporan untuk filter ini.',
  'reports.statusReady': 'Siap',
  'reports.statusPending': 'Menunggu',
  'reports.statusGenerating': 'Diproses',
  'reports.statusRegenerating': 'Update',
  'reports.statusFailed': 'Gagal',
  'reports.statusNoData': 'Tanpa transaksi',
  'reports.noTransactions': 'Tidak ada transaksi',
  'reports.noTransactionsDetail': 'Merchant ini tidak memiliki transaksi pada periode tersebut, sehingga tidak ada file laporan yang dibuat (menghemat penyimpanan server).',
  'reports.updatedBadge': 'Updated',
  'reports.regeneratedTooltip': 'Updated {date} — alasan: {reason}',
  'reports.reasonRecheckDrift': 'Data berubah saat recheck (callback telat / status flip)',
  'reports.reasonManualAdmin': 'Regenerate manual oleh admin',
  'reports.pendingRowsHint': 'pending — masih bisa berubah',
  'reports.download': 'Download',
  'reports.downloadTooltip': 'Download file XLSX',
  'reports.downloadDisabledTooltip':
    'File belum tersedia (belum di-generate atau tidak memiliki permission download).',
  'reports.openDetail': 'Lihat detail',
  'reports.detailTitle': 'Detail Laporan — {period}',
  'reports.fieldMerchant': 'Merchant',
  'reports.fieldStatus': 'Status',
  'reports.fieldVersion': 'Versi',
  'reports.fieldGeneratedBy': 'Di-generate oleh',
  'reports.fieldGeneratedAt': 'Waktu generate',
  'reports.fieldLastChecked': 'Terakhir di-recheck',
  'reports.fieldRows': 'Jumlah baris',
  'reports.fieldHash': 'Data hash',
  'reports.regeneratedAlert':
    'File ini sudah di-update setelah generate awal. Alasan: {reason}.',
  'reports.versionHistory': 'Riwayat versi',
  'reports.regenerateBtn': 'Regenerate sekarang',

  // ===== dashboard =====
  'dashboard.title': 'Overview',
  'dashboard.welcome': 'Selamat datang, {name}',
  'dashboard.scopeMerchant': 'Lingkup merchant',
  'dashboard.scopePlatform': 'Lingkup platform (semua merchant)',
  'dashboard.balanceOverview': 'Balance Overview',
  'dashboard.balanceOverviewHint': '— dijumlah dari semua merchant',
  'dashboard.pendingBalance': 'Pending Balance',
  'dashboard.pendingBalanceHint': 'Menunggu batas settlement',
  'dashboard.activeBalance': 'Active Balance',
  'dashboard.activeBalanceHint': 'Siap di-disburse',
  'dashboard.todayTrxCount': 'Total Trx Hari Ini',
  'dashboard.todayTrxCountHint': 'Dibuat hari ini (GMT+7)',
  'dashboard.totalOverview': 'Total Overview',
  'dashboard.totalOverviewHint': '— lifetime, hanya yang sudah settled',
  'dashboard.totalDeposit': 'Total Deposit',
  'dashboard.totalDepositHint': 'Jumlah nominal cash-in yang sudah settled',
  'dashboard.totalCountDeposit': 'Total Count Deposit',
  'dashboard.totalCountDepositHint': 'Transaksi deposit yang sudah paid',
  'dashboard.totalDisbursement': 'Total Disbursement',
  'dashboard.totalDisbursementHint': 'Jumlah nominal cash-out yang sudah settled',
  'dashboard.totalCountCashOut': 'Total Count Cash Out',
  'dashboard.totalCountCashOutHint': 'Transaksi disbursement yang sudah paid',
  'dashboard.charts': 'Chart',
  'dashboard.chartRangeLabel': '— {from} → {to} (30 hari terakhir)',
  'dashboard.chartAmountByDay': 'Daily Amount — Deposit vs Disbursement',
  'dashboard.chartDepositCountByMethod': 'Daily Deposit Count — per Method',
  'dashboard.chartDisbursementCountByMethod': 'Daily Disbursement Count — per Method',
  'dashboard.chartEmptyDeposit': 'Tidak ada data deposit pada rentang ini.',
  'dashboard.chartEmptyDisbursement':
    'Tidak ada data disbursement pada rentang ini. (withdraw-svc belum aktif)',
  'dashboard.balancesHidden':
    'Anda tidak memiliki permission untuk melihat balance overview.',
  'dashboard.totalsHidden': 'Total tidak terlihat untuk role Anda.',
  'dashboard.chartsNeedPermission':
    'Chart memerlukan permission `overview.charts.view`.',
  'dashboard.chartsFailedToLoad': 'Gagal memuat chart',
  'dashboard.overviewFailedToLoad': 'Gagal memuat overview',
  'dashboard.deposit': 'Deposit',
  'dashboard.disbursement': 'Disbursement',

  // ===== not found =====
  'notFound.message': 'Halaman tidak ditemukan.',
  'notFound.backToDashboard': 'Kembali ke Dashboard',

  // ===== deposits =====
  'deposits.title': 'Deposits',
  'deposits.subtitle': 'Transaksi cash-in',
  'deposits.filters': 'Filter',
  'deposits.activeFilters': '{n} aktif',
  'deposits.colTrxId': 'Trx ID',
  'deposits.colMerchantReff': 'Merchant Reff',
  'deposits.colMerchantName': 'Nama Merchant',
  'deposits.colCreatedAt': 'Dibuat',
  'deposits.colCreatedBy': 'Dibuat oleh',
  'deposits.colCreatedByPartial': 'nama merchant (partial)',
  'deposits.colRequestedFrom': 'Dari IP',
  'deposits.colMethod': 'Metode',
  'deposits.colStatus': 'Status',
  'deposits.colPaidAt': 'Dibayar',
  'deposits.colSettleDate': 'Estimasi Settle',
  'deposits.colSettlement': 'Settle Aktual',
  'deposits.colAmount': 'Nominal',
  'deposits.colFee': 'Fee',
  'deposits.colMerchantRevenue': 'Pendapatan Merchant',
  'deposits.placeholderTrxId': 'UUID persis',
  'deposits.placeholderReff': 'REF-...',
  'deposits.placeholderIp': '203.0.113.42',
  'deposits.placeholderAllMerchants': 'semua merchant',
  'deposits.placeholderAllMethods': 'semua metode',
  'deposits.placeholderAllStatuses': 'semua status',
  'deposits.placeholderDateRange': 'pilih rentang tanggal',
  'deposits.placeholderMin': 'min',
  'deposits.placeholderMax': 'max',
  'deposits.placeholderLoading': 'memuat…',
  'deposits.createdAtLabel': 'Created At (GMT+7)',
  'deposits.paidAtLabel': 'Paid At (GMT+7)',
  'deposits.settleAtLabel': 'Settlement Date (GMT+7)',
  'deposits.amountLabel': 'Nominal (IDR)',
  'deposits.feeLabel': 'Fee (IDR)',
  'deposits.merchantRevenueLabel': 'Pendapatan Merchant (IDR)',
  'deposits.empty': 'Belum ada transaksi.',
  'deposits.settlementSettled': 'settled',
  'deposits.settlementAwaiting': 'menunggu',

  // ===== disbursements =====
  'disbursements.title': 'Disbursements',
  'disbursements.subtitle': 'Cash-out via remit upstream. Wallet flow: active → reserved saat create, reserved → 0 (settled) saat callback sukses, reserved → active saat gagal.',
  'disbursements.filters': 'Filter',
  'disbursements.activeFilters': '{n} aktif',
  'disbursements.colTrxId': 'Trx ID',
  'disbursements.colMerchantReff': 'Merchant Reff',
  'disbursements.colMerchantName': 'Nama Merchant',
  'disbursements.colCreatedAt': 'Dibuat',
  'disbursements.colCreatedBy': 'Dibuat oleh',
  'disbursements.colCreatedByPartial': 'nama merchant (partial)',
  'disbursements.colRequestedFrom': 'Dari IP',
  'disbursements.colStatus': 'Status',
  'disbursements.colPaidAt': 'Dibayar',
  'disbursements.colBank': 'Bank',
  'disbursements.colBeneficiary': 'Penerima',
  'disbursements.colAmount': 'Nominal',
  'disbursements.colFee': 'Fee',
  'disbursements.colMerchantRevenue': 'Wallet Impact',
  'disbursements.placeholderTrxId': 'UUID persis',
  'disbursements.placeholderReff': 'WD-...',
  'disbursements.placeholderIp': '203.0.113.42',
  'disbursements.placeholderHolder': 'nama penerima (partial)',
  'disbursements.placeholderAllMerchants': 'semua merchant',
  'disbursements.placeholderAllBanks': 'semua bank',
  'disbursements.placeholderAllStatuses': 'semua status',
  'disbursements.placeholderDateRange': 'pilih rentang tanggal',
  'disbursements.placeholderMin': 'min',
  'disbursements.placeholderMax': 'max',
  'disbursements.createdAtLabel': 'Created At (GMT+7)',
  'disbursements.paidAtLabel': 'Paid At (GMT+7)',
  'disbursements.amountLabel': 'Nominal (IDR)',
  'disbursements.feeLabel': 'Fee (IDR)',
  'disbursements.empty': 'Belum ada disbursement.',

  // ===== wallet =====
  'wallet.title': 'Wallet',
  'wallet.subtitle': 'Saldo real-time. Update otomatis tiap 5 detik.',
  'wallet.merchantIdLabel': 'merchant id {id}',
  'wallet.merchantOnlyHint':
    'Halaman wallet hanya untuk merchant user. Internal user dapat membuka wallet lewat halaman merchant detail.',
  'wallet.internalTitle': 'Wallet — Semua Merchant',
  'wallet.internalSubtitle':
    'Total saldo agregat seluruh merchant + mutasi saldo lintas-merchant. Update otomatis tiap 5 detik.',
  'wallet.internalMerchantCount': '{count} merchant',
  'wallet.internalAccessDenied': 'Anda tidak memiliki izin untuk melihat wallet semua merchant.',
  'wallet.ledgerColMerchant': 'Merchant',
  'wallet.settlementTimingTitle': 'Waktu settlement — dua kali sehari',
  'wallet.settlementTimingIntro':
    'Deposit yang sudah dibayar masuk ke Pending dulu, lalu otomatis pindah ke Active satu kali. Secara default:',
  'wallet.settlementBatchMorning':
    'Dibayar pukul 12:00–23:59 GMT+7 → settle di hari berikutnya pukul 13:00.',
  'wallet.settlementBatchEvening':
    'Dibayar pukul 00:00–11:59 GMT+7 → settle di hari yang sama pukul 19:00.',
  'wallet.settlementBatchException':
    'Payment method tertentu (mis. BCA VA) settle H+1 dan melewati akhir pekan/hari libur. Hari dan jam settlement kedua window di atas bisa diatur admin di Platform Settings.',
  'wallet.settlementMeaning':
    'Jadi Pending = "diterima, menunggu settlement", dan Active = "siap di-disburse".',
  'wallet.balPending': 'Pending',
  'wallet.balActive': 'Active',
  'wallet.balReserved': 'Reserved',
  'wallet.balLifetimeDeposit': 'Lifetime Deposit',
  'wallet.balLifetimeDisbursement': 'Lifetime Disbursement',
  'wallet.balLifetimeFee': 'Lifetime Fee',
  'wallet.balPendingHint':
    'Belum settled — termasuk yang sudah paid dan menunggu settlement (default: hari yang sama pukul 19:00 atau hari berikutnya pukul 13:00 GMT+7)',
  'wallet.balActiveHint': 'Siap di-disburse',
  'wallet.balReservedHint': 'Disbursement in-flight',
  'wallet.ledgerTitle': 'Mutasi Saldo',
  'wallet.ledgerSubtitle': 'Mencatat settlement, penarikan, dan penyesuaian saldo (deposit tidak dicatat).',
  'wallet.mutSettlement': 'Settlement',
  'wallet.mutWithdrawal': 'Penarikan',
  'wallet.mutAdjustment': 'Penyesuaian Saldo',
  'wallet.mutFee': 'Biaya',
  'wallet.mutOther': 'Lainnya',
  'wallet.filters': 'Filter',
  'wallet.activeFilters': '{n} filter aktif',
  'wallet.filterMerchant': 'Merchant',
  'wallet.filterAnyMerchant': 'Semua merchant',
  'wallet.filterType': 'Tipe',
  'wallet.filterAnyType': 'Semua tipe',
  'wallet.filterTrxId': 'TRX ID',
  'wallet.filterTrxIdPlaceholder': 'Cari trxId…',
  'wallet.filterCreatedBy': 'Created By',
  'wallet.filterAnyCreatedBy': 'Semua',
  'wallet.filterApply': 'Terapkan',
  'wallet.filterClear': 'Reset',
  'wallet.ledgerColType': 'Tipe',
  'wallet.ledgerColTrxId': 'trxId',
  'wallet.ledgerColPending': 'Δ Pending',
  'wallet.ledgerColActive': 'Δ Active',
  'wallet.ledgerColReserved': 'Δ Reserved',
  'wallet.ledgerColCreatedBy': 'Dibuat oleh',
  'wallet.ledgerColCreated': 'Dibuat',
  'wallet.ledgerEmpty': 'Belum ada aktivitas ledger.',
  'wallet.pageRows': 'Halaman {page} · {count} baris',

  // ===== theme + locale switcher =====
  'theme.toggle': 'Ganti tema',
  'theme.autoUsing': 'Otomatis (sedang {scheme}) — klik untuk ke terang',
  'theme.darkClickToAuto': 'Gelap — klik untuk otomatis',
  'theme.lightClickToDark': 'Terang — klik untuk gelap',
  'locale.label': 'Bahasa',
  'locale.indonesian': 'Bahasa Indonesia',
  'locale.english': 'English',

  // ===== users =====
  'users.title': 'User',
  'users.subtitleMerchant': 'Kelola user di bawah merchant Anda.',
  'users.subtitleInternal': 'Kelola user internal TransPay & user merchant.',
  'users.activeFilters': '{n} aktif',
  'users.filterSearch': 'Cari',
  'users.filterSearchPh': 'Email atau nama',
  'users.filterMerchant': 'Merchant',
  'users.filterRole': 'Role',
  'users.filterRolePh': 'Semua role',
  'users.newUser': 'User Baru',
  'users.tabInternal': 'User Internal',
  'users.tabExternal': 'User Merchant',
  'users.colEmail': 'Email',
  'users.colName': 'Nama',
  'users.colRole': 'Role',
  'users.colMerchant': 'Merchant',
  'users.colLastLogin': 'Login terakhir',
  'users.colCreated': 'Dibuat',
  'users.empty': 'Belum ada user.',
  'users.badgeSuperuser': 'superuser',
  'users.noRole': '— tanpa role —',
  'users.menuIpWhitelist': 'IP whitelist',
  'users.menuEditUser': 'Edit user',
  'users.menuResetMfa': 'Reset MFA',
  'users.menuMfaInactive': 'MFA tidak aktif',
  'users.resetMfaTitle': 'Reset MFA — {email}',
  'users.resetMfaWarning':
    'Tindakan ini menonaktifkan MFA user {email}. User harus enroll ulang via halaman Security sebelum bisa melakukan disbursement lagi. Aksi dicatat di audit log.',
  'users.resetMfaConfirm': 'Reset MFA sekarang',
  // --- Suspend state (migration 0014) ---
  'users.suspendedBadge': 'Suspended',
  'users.menuUnsuspend': 'Cabut suspend',
  'users.unsuspendTitle': 'Cabut suspend — {email}',
  'users.unsuspendWarning':
    'User {email} akan diaktifkan kembali dan counter login gagal di-reset ke 0. Aksi dicatat di audit log.',
  'users.unsuspendConfirm': 'Cabut suspend',
  'users.suspendReason.too_many_failed_logins': 'Terlalu banyak login gagal',
  'users.suspendReason.merchant_suspended_cascade': 'Ikut suspend karena merchant',
  'users.suspendReason.manual': 'Manual oleh admin',
  'users.suspendDetail': 'Sejak {date} · {reason}',
  'users.ipWhitelistTitle': 'IP Whitelist · {email}',
  'users.ipWhitelistDenyAll':
    'Deny-all default. Tanpa minimal 1 entry user ini akan kena {token} di login berikutnya. Saat rotate IP, tambah dulu IP baru sebelum hapus yang lama.',
  'users.ipWhitelistAddCidr': 'Tambah IP',
  'users.ipWhitelistCidrLabel': 'Alamat IP',
  'users.ipWhitelistCidrPlaceholder': '203.0.113.42',
  'users.ipWhitelistRequired': 'Wajib diisi',
  'users.ipWhitelistInvalidIp': 'Alamat IPv4 tidak valid (contoh: 203.0.113.42)',
  'users.ipWhitelistLabelLabel': 'Label',
  'users.ipWhitelistLabelPlaceholder': 'HQ Jakarta / VPN',
  'users.ipWhitelistExisting': 'Entri yang ada',
  'users.ipWhitelistColCidr': 'IP',
  'users.ipWhitelistColLabel': 'Label',
  'users.ipWhitelistColEnabled': 'Aktif',
  'users.ipWhitelistColCreatedBy': 'Dibuat oleh',
  'users.ipWhitelistColLastUsed': 'Terakhir dipakai',
  'users.ipWhitelistEmpty':
    'Belum ada IP whitelist. User ini tidak akan bisa login sampai ada minimal satu entry.',
  'users.ipWhitelistEnabledYes': 'ya',
  'users.ipWhitelistEnabledNo': 'tidak',
  'users.ipWhitelistRemove': 'Hapus',
  'users.ipWhitelistClose': 'Tutup',
  'users.createTitle': 'Buat user {scope}',
  'users.emailLabel': 'Email',
  'users.accountNameLabel': 'Nama akun',
  'users.initialPasswordLabel': 'Password awal',
  'users.initialPasswordDesc': 'min 12 karakter; user bisa mengubah setelah login pertama',
  'users.merchantLabel': 'Merchant',
  'users.merchantDesc':
    'Merchant tempat user ini bekerja. User hanya akan melihat data dari merchant ini.',
  'users.merchantPlaceholderLoading': 'memuat…',
  'users.merchantPlaceholderSelect': 'Pilih merchant',
  'users.merchantNothingFound': 'Tidak ada merchant',
  'users.roleLabel': 'Role',
  'users.roleDesc': 'Permission diberikan melalui penetapan role.',
  'users.newPasswordLabel': 'Password baru',
  'users.newPasswordDesc': 'kosongkan untuk tetap menggunakan yang lama (min 12 karakter)',
  'users.editTitle': 'Edit {email}',
  'users.deleteTitle': 'Hapus user',
  'users.deleteConfirm': 'Hapus user {email}?',
  'users.deleteWarning': 'Aksi ini permanen. User tidak bisa login lagi setelah dihapus.',

  // ===== roles =====
  'roles.title': 'Role & Permission',
  'roles.subtitle': 'Atur permission per role. Sistem role tetap bisa di-edit grant-nya.',
  'roles.newRole': 'Role Baru',
  'roles.internalOnlyAlert':
    'Halaman ini hanya bisa diakses oleh staff internal TransPay. Hubungi admin Anda jika Anda butuh perubahan role.',
  'roles.noManagePermission':
    'Anda tidak punya permission untuk mengatur role. Minta admin untuk grant {permA} atau {permB}.',
  'roles.tabInternal': 'Role Internal',
  'roles.tabMerchant': 'Role Merchant',
  'roles.empty': 'Tidak ada role.',
  'roles.selectPrompt': 'Pilih role di kiri.',
  'roles.badgeSystem': 'system',
  'roles.savedSummary':
    'Tersimpan — {added} ditambahkan, {removed} dihapus, {affected} sesi user di-cache di-invalidate.',
  'roles.uncatalogedKeys':
    'Permission key baru ditemukan yang belum masuk ke katalog fitur — tidak bisa di-toggle dari UI ini: {keys}',
  'roles.colCreate': 'Buat',
  'roles.colView': 'Lihat',
  'roles.colEdit': 'Edit',
  'roles.colSuspend': 'Suspend / Hapus',
  'roles.granted': '{granted} / {total} diberikan',
  'roles.clearAll': 'Bersihkan semua',
  'roles.grantAll': 'Berikan semua',
  'roles.colFeature': 'FITUR',
  'roles.colExtras': 'EKSTRA',
  'roles.appliesInternal': 'Internal',
  'roles.appliesMerchant': 'Merchant',
  'roles.appliesBoth': 'Keduanya',
  'roles.tooltipInternal': 'Hanya staff internal TransPay',
  'roles.tooltipMerchant': 'Merchant + sub-user di bawah merchant',
  'roles.tooltipBoth': 'Internal & merchant — keduanya',
  'roles.createTitle': 'Buat Role',
  'roles.nameLabel': 'Nama',
  'roles.slugLabel': 'Slug',
  'roles.slugDesc': 'identifier wire lower_snake_case, mis. merchant_ops_lead',
  'roles.scopeLabel': 'Scope',
  'roles.descriptionLabel': 'Deskripsi',
  // ----- feature catalog -----
  'roles.catBusiness': 'Aktivitas Bisnis',
  'roles.catBusinessHint': 'Halaman utama yang dilihat user setiap hari.',
  'roles.catMerchantOps': 'Manajemen Merchant',
  'roles.catMerchantOpsHint':
    'Onboarding & operasional merchant — sebagian besar untuk staff internal TransPay.',
  'roles.catUsersRoles': 'Pengguna & Role',
  'roles.catUsersRolesHint': 'Akses ke daftar user, undangan, dan pengaturan role.',
  'roles.catWallet': 'Wallet & Keuangan',
  'roles.catWalletHint': 'Saldo, ledger, dan penyesuaian wallet.',
  'roles.catSecurity': 'Keamanan & Akses API',
  'roles.catSecurityHint': 'API credentials & IP whitelist.',
  'roles.catAudit': 'Audit & Sistem',
  'roles.catAuditHint': 'Riwayat aktivitas, jejak API, dan setting platform.',
  'roles.featOverview': 'Dashboard / Overview',
  'roles.featDeposits': 'Deposit (Cash-in)',
  'roles.featDisbursements': 'Disbursement (Cash-out)',
  'roles.featDisbursementsDesc': 'Hanya merchant yang dapat membuat (create) disbursement.',
  'roles.featTransactionReport': 'Laporan Transaksi',
  'roles.featTransactionReportDesc':
    'File XLSX harian (cashin + cashout) yang di-generate otomatis. View untuk melihat list, Download untuk unduh file.',
  'roles.extraDownloadTransactionReport': 'Download file laporan',
  'roles.extraViewAllMerchantReports': 'Lihat laporan semua merchant (internal-only)',
  'roles.featMerchants': 'Merchant',
  'roles.featWhDeliveryOwn': 'Webhook Delivery (Sendiri)',
  'roles.featWhDeliveryAll': 'Webhook Delivery (Semua Merchant)',
  'roles.featSubUsers': 'Sub-User Merchant',
  'roles.featSubUsersDesc': 'Akun staff di dalam satu merchant.',
  'roles.featInternalUsers': 'Internal User (TransPay)',
  'roles.featInternalUsersDesc': 'Staff TransPay (admin, ops, finance, CS).',
  'roles.featResetMfa': 'Reset MFA User',
  'roles.featResetMfaDesc': 'Nonaktifkan MFA user lain agar bisa enroll ulang (mis. perangkat hilang). Internal: semua user; merchant admin: user dalam merchant sendiri.',
  'roles.featRoles': 'Role & Permission',
  'roles.featRolesDesc': 'Siapa yang boleh mengatur role mana. Hanya staff internal.',
  'roles.featWalletOwn': 'Wallet (Sendiri)',
  'roles.featWalletAny': 'Wallet (Merchant Manapun)',
  'roles.featApiCredentials': 'API Credentials (Sendiri)',
  'roles.extraRevealToken': 'Reveal Token (Plaintext)',
  'roles.featIpwUser': 'IP Whitelist — User Login',
  'roles.featIpwMerchant': 'IP Whitelist — Merchant API',
  'roles.featIpwInternal': 'IP Whitelist — Internal Users',
  'roles.featAuditLog': 'Audit Log',
  'roles.featApiActivity': 'API Activity (per Transaksi)',
  'roles.featApiActivityDesc': 'Hanya staff internal yang melihat jejak ke upstream provider.',
  'roles.featSettings': 'Pengaturan Platform',
  'roles.featApiDocs': 'Dokumentasi API',
  'roles.featApiDocsDesc': 'Akses halaman Dokumentasi API. Daftar endpoint di dalamnya tetap difilter sesuai permission user.',
  'roles.extraViewBalances': 'Lihat Saldo',
  'roles.extraViewTrxCount': 'Lihat Jumlah Trx',
  'roles.extraViewCharts': 'Lihat Grafik',
  'roles.extraViewDetail': 'Lihat Detail',
  'roles.extraManageCredentials': 'Kelola Kredensial',
  'roles.extraManageWebhookUrl': 'Kelola Webhook URL',
  'roles.extraViewAllMerchants': 'Lihat Semua Merchant',
  'roles.extraUnsuspendMerchant': 'Buka Suspend Merchant',
  'roles.extraUnsuspendUser': 'Buka Suspend User',
  'roles.extraResetUserMfa': 'Reset MFA User',
  'roles.extraRetry': 'Retry',
  'roles.extraMarkDead': 'Tandai Mati',
  'roles.extraUpdateStatus': 'Update status (manual)',
  'roles.extraManageMerchantRole': 'Kelola Role Merchant',
  'roles.extraManageInternalRole': 'Kelola Role Internal',
  'roles.extraDelegate': 'Delegasi',
  'roles.extraViewLedger': 'Lihat Ledger',
  'roles.extraAdjustAudited': 'Adjust (Audited)',
  'roles.extraViewPayokActivity': 'Lihat PAYOK Activity',
  'roles.extraViewPaymentMethods': 'Lihat Metode Pembayaran Merchant',
  'roles.extraManagePaymentMethods': 'Kelola Metode Pembayaran Merchant',
  'roles.extraManageApiDisbursement': 'Kelola Gate Disbursement API (uang keluar)',
  'roles.extraManageAcquirers': 'Kelola Acquirer',

  // ===== settings =====
  'settings.title': 'Platform Settings',
  'settings.subtitle':
    'Konfigurasi bisnis yang dapat diatur operator. Setiap perubahan tersimpan di DB dan tercatat di audit log. Reset ke default untuk kembali ke nilai hardcoded di kode.',
  'settings.reload': 'Reload',
  'settings.superuserOnly': 'Hanya superadmin yang dapat mengakses Platform Settings.',
  'settings.failedToLoad': 'Gagal memuat settings',
  'settings.countSingular': '{n} setting',
  'settings.countPlural': '{n} setting',
  'settings.overrideBadge': 'override operator',
  'settings.defaultBadge': 'default kode',
  'settings.savedAlert':
    'Tersimpan. Cache di-invalidate; pembacaan berikutnya akan mengambil nilai baru dalam 1 menit.',
  'settings.defaultLabel': 'Default:',
  'settings.lastChanged': '· terakhir diubah oleh {user} pada {at}',
  'settings.resetToDefault': 'Reset ke default',
  'settings.catSettlement': 'Settlement',
  'settings.catPaymentMethods': 'Payment Methods',
  'settings.catFees': 'Fees',
  'settings.catAuth': 'Auth',
  'settings.catWebhook': 'Webhook',
  'settings.settlementWindowDaysLabel': 'Selisih hari',
  'settings.settlementWindowDaysDesc': '0 = hari yang sama, 1 = hari berikutnya, dst.',
  'settings.settlementWindowHourLabel': 'Jam settlement (GMT+7)',
  'settings.settlementWindowHourDesc': '{min}–23',
  'settings.settlementWindowPreviewSameDay': 'Settle di hari yang sama pukul {hour}:00 GMT+7.',
  'settings.settlementWindowPreviewNextDay': 'Settle di hari berikutnya pukul {hour}:00 GMT+7.',
  'settings.settlementWindowPreviewDays': 'Settle {n} hari setelah dibayar, pukul {hour}:00 GMT+7.',
  'settings.enabledMethodsLabel': 'Metode aktif',
  'settings.basisPointsLabel': 'Basis points',
  'settings.basisPointsDesc': '100 bps = 1%',
  'settings.idrLabel': 'IDR',
  'settings.catSettlementException': 'Custom Settlement Exception',
  'settings.exc.methodsLabel': 'Payment method',
  'settings.exc.methodsDesc':
    'Centang payment method yang memakai aturan exception. Method yang tidak dicentang mengikuti dua window settlement biasa di atas.',
  'settings.exc.datesLabel': 'Jam settlement & tanggal exception per payment method',
  'settings.exc.datesDesc':
    'Method yang dicentang settle H+1 pada jam settlement-nya sendiri. Tanggal exception adalah tanggal yang DILEWATI settlement. Sabtu & Minggu otomatis terpilih — klik tanggal di kalender untuk memilih atau membatalkan. Tanggal yang sudah lewat tidak dapat dipilih.',
  'settings.exc.pickDates': 'Pilih tanggal exception',
  'settings.exc.noneChecked': 'Belum ada payment method yang dicentang — semua settlement mengikuti dua window settlement biasa.',
  'settings.exc.hourLabel': 'Jam settlement (0-23, GMT+7)',
  'settings.exc.summaryHour': 'H+1 pukul {hour}:00',
  'settings.exc.weekendDefault': 'melewati setiap Sabtu & Minggu',
  'settings.exc.extraCount': '+{n} tanggal tambahan',
  'settings.exc.weekendOffCount': '{n} weekend dibatalkan',
  'settings.exc.extraDates': 'Tanggal tambahan:',
  'settings.exc.weekendOffDates': 'Weekend yang dibatalkan (settlement boleh jatuh di sini):',
  'settings.exc.calendarHint': 'Terpilih = dilewati settlement (GMT+7)',
  'settings.noEditorTitle':
    'Belum ada editor khusus untuk setting ini. Menampilkan JSON untuk inspeksi saja — edit langsung dari API jika perlu.',

  // ===== merchants =====
  'merchants.title': 'Merchant',
  'merchants.subtitle': 'Kelola merchant onboarded',
  'merchants.newMerchant': 'Merchant Baru',
  'merchants.colId': 'id',
  'merchants.colName': 'Nama',
  'merchants.colEmail': 'Email',
  'merchants.colDepositFee': 'Fee deposit (default)',
  'merchants.colDisbFee': 'Fee disb (default)',
  'merchants.feeOverrideVa': '{n} VA',
  'merchants.feeOverrideType': '{n} tipe',
  'merchants.feeOverrideVaTitle': 'Fee VA',
  'merchants.feeOverrideVaSub': 'metode lain pakai default',
  'merchants.feeOverrideVaDefault': 'Default (non-VA)',
  'merchants.feeOverrideDisbTitle': 'Fee disbursement',
  'merchants.feeOverrideDisbSub': 'tipe lain pakai default',
  'merchants.feeOverrideDisbDefault': 'Default',
  'merchants.colActiveBalance': 'Saldo Aktif',
  'merchants.colPendingBalance': 'Saldo Pending',
  'merchants.colCashIn': 'Total Cash In',
  'merchants.colCashOut': 'Total Cash Out',
  'merchants.colApi': 'API',
  'merchants.colStatus': 'Status',
  'merchants.colCreated': 'Dibuat',
  'merchants.empty': 'Belum ada merchant. Klik Merchant Baru untuk membuat.',
  'merchants.activeFilters': '{n} aktif',
  'merchants.filterSearch': 'Cari',
  'merchants.filterSearchPh': 'Nama atau email merchant',
  'merchants.filterStatus': 'Status',
  'merchants.filterApi': 'Akses API',
  'merchants.filterAll': 'Semua',
  'merchants.apiEnabled': 'aktif',
  'merchants.apiDisabled': 'nonaktif',
  'merchants.statusSuspended': 'disuspend',
  'merchants.statusActive': 'aktif',
  // --- Suspend actions (migration 0014) ---

  // ===== api credentials =====
  'apiCred.title': 'API Credentials',
  'apiCred.merchantOnly': 'Halaman API Credentials hanya untuk merchant user.',
  'apiCred.subtitle':
    'Kredensial untuk integrasi server-to-server ke `/api/v1/cashin` dan `/api/v1/cashout`',
  'apiCred.activeCredentials': 'Kredensial aktif',
  'apiCred.viewToken': 'Lihat token',
  'apiCred.regenerateToken': 'Regenerate token',
  'apiCred.clientIdLabel': 'Client ID',
  'apiCred.clientIdDesc': 'Identifier publik — boleh disimpan di repo / config.',
  'apiCred.clientTokenLabel': 'Client Token',
  'apiCred.webhookSecretLabel': 'Webhook Secret',
  'apiCred.webhookSecretDesc': 'Kunci HMAC untuk memverifikasi webhook masuk dan menandatangani request disbursement API. Rahasia — simpan aman.',
  'apiCred.tokenHiddenAdmin':
    'Token tersembunyi (verifikasi argon2id hash di auth path). Klik "Lihat token" — masukkan password Anda untuk reveal plaintext (di-audit).',
  'apiCred.tokenHiddenNonAdmin':
    'Token tersembunyi (verifikasi argon2id hash di auth path). Plaintext hanya bisa di-reveal oleh user yang punya izin "Reveal Own API Token". Hubungi admin Anda untuk meminta akses.',
  'apiCred.apiAccess': 'API Access',
  'apiCred.usageHttp': 'Cara pakai (HTTP)',
  'apiCred.merchantUpdated': 'Merchant diperbarui: {at}',
  'apiCred.errInvalidPassword': 'Password salah. Coba lagi.',
  'apiCred.errTokenNotRevealable':
    'Token belum di-encrypt di DB (merchant lama, sebelum fitur reveal). Klik Regenerate token sekali untuk mengaktifkan.',
  'apiCred.errKmsDecrypt': 'KMS gagal decrypt. Cek konfigurasi KMS / kontak admin platform.',
  'apiCred.errPasswordRequired': 'Password wajib diisi.',
  'apiCred.verifyPasswordTitle': 'Verifikasi password',
  'apiCred.verifyPasswordIntro':
    'Untuk reveal token plaintext, masukkan ulang password akun Anda. Tindakan ini di-log di audit dengan email + IP Anda.',
  'apiCred.accountPasswordLabel': 'Password akun',
  'apiCred.mfaCodeLabel': 'Kode MFA',
  'apiCred.mfaCodeHint': 'Isi jika akun Anda mengaktifkan MFA (kode 6 digit dari authenticator, atau backup code).',
  'apiCred.mfaCodePlaceholder': '123456',
  'apiCred.errMfaRequired': 'Akun Anda mengaktifkan MFA — masukkan kode MFA untuk reveal token.',
  'apiCred.errInvalidMfaCode': 'Kode MFA salah atau kedaluwarsa. Coba lagi.',
  'apiCred.errMfaUnavailable': 'Verifikasi MFA sedang tidak tersedia. Coba lagi atau hubungi admin.',
  'apiCred.tokenPlaintextTitle': 'Token plaintext',
  'apiCred.tokenRevealedWarning':
    'Ini token aktif Anda. Tindakan ini di-log di audit. Pastikan disimpan di tempat aman dan tutup modal setelah selesai.',
  'apiCred.close': 'Tutup',
  'apiCred.copied': 'Tersalin',
  'apiCred.copy': 'Salin',

  // ===== merchant detail =====
  'merchantDetail.back': 'Kembali',
  'merchantDetail.copied': 'Tersalin',
  'merchantDetail.copy': 'Salin',
  'merchantDetail.cancel': 'Batal',
  'merchantDetail.close': 'Tutup',
  'merchantDetail.reset': 'Reset',
  'merchantDetail.badgeSuspended': 'Disuspend',
  'merchantDetail.badgeActive': 'Aktif',
  'merchantDetail.suspend': 'Suspend',
  'merchantDetail.unsuspend': 'Aktifkan kembali',
  'merchantDetail.revealToken': 'Reveal token',
  'merchantDetail.regenerateCredentials': 'Regenerate credentials',
  'merchantDetail.tabInfo': 'Info',
  'merchantDetail.tabCredentials': 'Credentials',
  'merchantDetail.tabIpWhitelist': 'API IP Whitelist',
  'merchantDetail.tabPayok': 'PAYOK Settings',
  'merchantDetail.tabWallet': 'Wallet',
  'merchantDetail.tabPaymentMethods': 'Payment Methods',
  'merchantDetail.pmTitle': 'Cash-in payment methods',
  'merchantDetail.pmDesc': 'Toggle which cash-in payment methods this merchant may use. Limited to the methods enabled platform-wide.',
  'merchantDetail.pmReadOnly': 'Read-only — you can view but not edit these payment methods.',
  'merchantDetail.pmInheriting': 'Inheriting the platform default — all available methods are enabled for this merchant.',
  'merchantDetail.pmRestricted': 'Restricted — {n} method(s) enabled for this merchant.',
  'merchantDetail.pmSelectAll': 'Select all',
  'merchantDetail.pmSave': 'Save payment methods',
  'merchantDetail.pmNoneWarning': 'At least one method must stay enabled.',
  'merchantDetail.apiDisbTitle': 'Server-to-server disbursement (API)',
  'merchantDetail.apiDisbDesc': 'Allow this merchant to initiate withdrawals (money-out) via the API key, not just the dashboard.',
  'merchantDetail.apiDisbWarning': 'High-risk: a leaked API key from a whitelisted IP could move money out. Enable only for trusted, integrated merchants. Requests must be HMAC-signed.',
  'merchantDetail.vaFeesTab': 'VA Fees',
  'merchantDetail.vaFeesDesc': 'Cash-in fee per Virtual Account method: a percentage (%) PLUS a flat IDR amount, both applied — fee = round(amount × %) + flat. Leave both fields blank to fall back to the merchant percentage-only deposit fee for that VA. QRIS, e-wallet and OTC always use the percentage fee.',
  'merchantDetail.vaFeesFallback': 'blank = % deposit fee',
  'merchantDetail.vaFeesPercentLabel': 'Percent (%)',
  'merchantDetail.vaFeesFixedLabel': 'Flat (IDR)',
  'merchantDetail.vaFeesSave': 'Save VA fees',
  'merchantDetail.disbursementFeesTab': 'Disbursement Fees',
  'merchantDetail.disbursementFeesDesc': 'Cash-out fee per beneficiary account type (Bank, E-wallet, BPR): a percentage (%) PLUS a flat IDR amount, both applied on the amount — fee = round(amount × %) + flat. The fee is added on top; the beneficiary still receives the full amount. Leave both fields blank to fall back to the merchant flat default disbursement fee for that type.',
  'merchantDetail.disbursementFeesFallback': 'blank = default fee',
  'merchantDetail.disbursementFeesSave': 'Save disbursement fees',
  // credentials tab
  'merchantDetail.transpayClientId': 'Transpay Client ID',
  'merchantDetail.tokenSuffix': 'Token suffix',
  'merchantDetail.tokenSuffixHint': 'Auth path verifies via argon2id hash.',
  'merchantDetail.tokenSuffixHintAdmin':
    'Klik Reveal token di header untuk lihat plaintext (KMS decrypt, audited).',
  'merchantDetail.tokenSuffixHintNonAdmin': 'Plaintext tidak ditampilkan di sini.',
  // password prompt modal
  'merchantDetail.verifyPasswordTitle': 'Verifikasi password',
  'merchantDetail.verifyPasswordIntro':
    'Untuk reveal token plaintext, masukkan ulang password akun Anda. Tindakan ini di-log di audit dengan email + IP Anda.',
  'merchantDetail.accountPasswordLabel': 'Password akun',
  'merchantDetail.errPasswordWrong': 'Password salah. Coba lagi.',
  'merchantDetail.errTokenNotRevealable':
    'Token belum di-encrypt (merchant lama, sebelum fitur reveal). Klik Regenerate credentials sekali untuk mengaktifkan.',
  'merchantDetail.errKmsDecrypt': 'KMS gagal decrypt. Cek konfigurasi KMS.',
  'merchantDetail.errPasswordRequired': 'Password wajib diisi.',
  // token reveal modal
  'merchantDetail.tokenRotatedTitle': 'New credentials issued',
  'merchantDetail.tokenPlaintextTitle': 'Token plaintext',
  'merchantDetail.tokenRotatedWarning':
    'Token lama tidak valid lagi. Plaintext token hanya ditampilkan di sini — copy & simpan sekarang.',
  'merchantDetail.tokenRevealedWarning':
    'Token aktif merchant. Tindakan reveal di-log di audit_log dengan email + IP. Tutup modal setelah selesai.',
  'merchantDetail.transpayClientToken': 'Transpay Client Token',
  'merchantDetail.webhookSecret': 'Webhook Secret',
  // IP whitelist panel
  'merchantDetail.ipwlDenyAll':
    'Default deny-all. Tanpa minimal 1 IP di-whitelist, `POST /api/v1/cashin` akan return 403 `ip_whitelist_required`.',
  'merchantDetail.ipwlAddCidr': 'Tambahkan IP',
  'merchantDetail.ipwlCidrLabel': 'Alamat IP',
  'merchantDetail.ipwlCidrPlaceholder': '203.0.113.42',
  'merchantDetail.ipwlLabelLabel': 'Label',
  'merchantDetail.ipwlLabelPlaceholder': 'dev / merchant-prod / dll',
  'merchantDetail.ipwlRequired': 'Wajib diisi',
  'merchantDetail.ipwlInvalidIp': 'Alamat IPv4 tidak valid (contoh: 203.0.113.42)',
  'merchantDetail.ipwlColCidr': 'IP',
  'merchantDetail.ipwlColLabel': 'Label',
  'merchantDetail.ipwlColCreatedBy': 'Dibuat oleh',
  'merchantDetail.ipwlColCreated': 'Dibuat',
  'merchantDetail.ipwlColLastUsed': 'Terakhir dipakai',
  'merchantDetail.ipwlRemove': 'Hapus',
  'merchantDetail.ipwlEmpty': 'Belum ada entry. Tambahkan minimal 1 untuk mengaktifkan API.',
  // merchant info edit panel
  'merchantDetail.identityReadOnly': 'Identity (read-only)',
  'merchantDetail.fieldMerchantId': 'Merchant ID',
  'merchantDetail.fieldCreated': 'Dibuat',
  'merchantDetail.fieldLastUpdated': 'Terakhir diperbarui',
  'merchantDetail.fieldStatus': 'Status',
  'merchantDetail.statusSuspendedAt': 'Disuspend pada {at}',
  'merchantDetail.statusActive': 'Aktif',
  'merchantDetail.basics': 'Dasar',
  'merchantDetail.nameLabel': 'Nama',
  'merchantDetail.emailLabel': 'Email',
  'merchantDetail.apiAccessEnabled': 'API access aktif',
  'merchantDetail.apiAccessEnabledDesc': 'Bila disabled, /api/v1/cashin akan reject 403',
  'merchantDetail.fees': 'Fee',
  'merchantDetail.depositFeeBps': 'Fee deposit (bps)',
  'merchantDetail.depositFeeBpsDesc': '{pct} % per transaksi cash-in',
  'merchantDetail.disbursementFeeIdr': 'Fee disbursement (IDR)',
  'merchantDetail.disbursementFeeIdrDesc': 'Flat IDR per transaksi cash-out',
  'merchantDetail.transactionLimits': 'Batas Transaksi (IDR per transaksi)',
  'merchantDetail.transactionLimitsDesc':
    'Batas amount per transaksi cash-in dan cash-out untuk merchant ini. Tidak ada batas harian — hanya per-transaksi. Saat user kirim amount di luar range, request langsung di-reject sebelum ke PAYOK (deposit) atau sebelum reserve wallet (disbursement).',
  'merchantDetail.cashinMin': 'Cash-in min',
  'merchantDetail.cashinMinDesc': 'Amount minimum per transaksi deposit',
  'merchantDetail.cashinMax': 'Cash-in max',
  'merchantDetail.cashinMaxDesc': 'Amount maksimum per transaksi deposit',
  'merchantDetail.cashoutMin': 'Cash-out min',
  'merchantDetail.cashoutMinDesc': 'Amount minimum per transaksi disbursement',
  'merchantDetail.cashoutMax': 'Cash-out max',
  'merchantDetail.cashoutMaxDesc':
    'Amount maksimum per transaksi disbursement (PAYOK IDN cap: Rp 50.000.000)',
  'merchantDetail.webhookUrls': 'Webhook URL (TransPay → Merchant)',
  'merchantDetail.webhookUrlsDesc':
    'TransPay akan POST signed event ke URL ini setelah transaksi settle. Header X-Transpay-Signature dihitung HMAC-SHA256 dari webhookSecret merchant.',
  'merchantDetail.depositWebhookUrl': 'Deposit webhook URL',
  'merchantDetail.disbursementWebhookUrl': 'Disbursement webhook URL',
  'merchantDetail.changesSaved': 'Perubahan tersimpan.',
  'merchantDetail.saveChanges': 'Simpan perubahan',
  // validation
  'merchantDetail.valRequired': 'Wajib diisi',
  'merchantDetail.valInvalidEmail': 'Email tidak valid',
  'merchantDetail.valInvalidUrl': 'URL tidak valid (harus diawali http:// atau https://)',
  'merchantDetail.valDepositFeeRange': '0–10000 (1 bps = 0.01 %)',
  'merchantDetail.valDisbFeeRange': '0–999,999,999',
  'merchantDetail.valMinRp1': 'min Rp 1',
  'merchantDetail.valMaxGteMin': 'max harus ≥ min',
  // PAYOK settings panel
  'merchantDetail.payokEnabledTitle': 'PAYOK V3.6 integration {state}',
  'merchantDetail.payokStateEnabled': 'enabled',
  'merchantDetail.payokStateDisabled': 'disabled',
  'merchantDetail.payokConnection': 'Connection',
  'merchantDetail.payokEnable': 'Aktifkan PAYOK',
  'merchantDetail.payokEnableDesc': 'Aktif/nonaktifkan pembayaran untuk merchant ini melalui acquirer default.',
  'merchantDetail.payokManagedNote': 'Konfigurasi koneksi (base URL, MID, endpoint, kunci, URL notifikasi) kini dikelola terpusat di menu Acquirer. Di sini hanya tersedia tombol aktif/nonaktif untuk merchant ini.',
  'merchantDetail.payokSaveSettings': 'Simpan PAYOK settings',
  'merchantDetail.payokUnavailable':
    'Status PAYOK merchant ini hanya dapat dilihat oleh user dengan izin edit merchant.',
  // rotate keypair confirm modal
  // rotate keypair result modal
  // wallet panel
  'merchantDetail.balances': 'Saldo',
  'merchantDetail.adjustActiveBalance': 'Adjust saldo aktif',
  'merchantDetail.balPending': 'Pending Balance',
  'merchantDetail.balActive': 'Active Balance',
  'merchantDetail.balReserved': 'Reserved Balance',
  'merchantDetail.balOutstanding': 'Outstanding (carry-over)',
  'merchantDetail.balOutstandingValue': '{amount} — auto-deducted from next settlements',
  'merchantDetail.balLifetimeDeposit': 'Lifetime Deposit',
  'merchantDetail.balLifetimeDisbursement': 'Lifetime Disbursement',
  'merchantDetail.balLifetimeFee': 'Lifetime Fee',
  'merchantDetail.balUpdated': 'Updated',
  // adjust balance modal
  'merchantDetail.adjustCurrentActive': 'Saldo aktif saat ini: ',
  'merchantDetail.adjustOutstanding': ' · Outstanding: ',
  'merchantDetail.adjustAdd': 'Tambah',
  'merchantDetail.adjustDeduct': 'Kurangi',
  'merchantDetail.adjustAmount': 'Amount (IDR)',
  'merchantDetail.adjustExceedsPre': 'Pengurangan melebihi saldo aktif sebesar ',
  'merchantDetail.adjustExceedsMid': '{deducted} akan dikurangi sekarang; sisa ',
  'merchantDetail.adjustExceedsPost':
    ' bisa dialihkan (carry-over) ke settlement berikutnya sampai lunas.',
  'merchantDetail.adjustCarryLabel': 'Alihkan {amount} ke settlement berikutnya',
  'merchantDetail.adjustReason': 'Alasan',
  'merchantDetail.adjustReasonDesc':
    'Dicatat di wallet ledger dan audit log (min 10 karakter).',
  'merchantDetail.adjustReasonPlaceholder':
    'mis. Koreksi manual untuk chargeback ref #1234',
  'merchantDetail.adjustReasonError': 'Minimal 10 karakter',
  'merchantDetail.adjustAddToActive': 'Tambah ke saldo aktif',
  'merchantDetail.adjustDeductFromActive': 'Kurangi dari saldo aktif',

  // ===== notifications (toast wording, per action) =====
  'notify.user.create.ok': 'User berhasil dibuat',
  'notify.user.create.fail': 'Gagal membuat user',
  'notify.user.update.ok': 'User berhasil diperbarui',
  'notify.user.update.fail': 'Gagal memperbarui user',
  'notify.user.delete.ok': 'User berhasil dihapus',
  'notify.user.delete.fail': 'Gagal menghapus user',
  'notify.user.selfEditTitle': 'Tidak diizinkan',
  'notify.user.selfEditMsg': 'Anda tidak dapat mengedit akun Anda sendiri.',
  'notify.mfa.reset.ok': 'MFA user direset',
  'notify.mfa.reset.fail': 'Gagal mereset MFA',
  'notify.user.unsuspend.ok': 'Suspend user dibatalkan',
  'notify.user.unsuspend.fail': 'Gagal membatalkan suspend user',
  'notify.mfa.enable.ok': 'MFA berhasil diaktifkan',
  'notify.mfa.enable.fail': 'Gagal mengaktifkan MFA',
  'notify.mfa.disable.ok': 'MFA dinonaktifkan',
  'notify.mfa.disable.fail': 'Gagal menonaktifkan MFA',
  'notify.ipwl.add.ok': 'IP whitelist ditambahkan',
  'notify.ipwl.add.fail': 'Gagal menambah IP whitelist',
  'notify.ipwl.remove.ok': 'IP whitelist dihapus',
  'notify.ipwl.remove.fail': 'Gagal menghapus IP whitelist',
  'notify.role.save.ok': 'Role berhasil disimpan',
  'notify.role.save.fail': 'Gagal menyimpan role',
  'notify.role.create.ok': 'Role berhasil dibuat',
  'notify.role.create.fail': 'Gagal membuat role',
  'notify.setting.save.ok': 'Pengaturan berhasil disimpan',
  'notify.setting.save.fail': 'Gagal menyimpan pengaturan',
  'notify.setting.reset.ok': 'Pengaturan dikembalikan ke default',
  'notify.setting.reset.fail': 'Gagal mereset pengaturan',
  'notify.merchant.create.ok': 'Merchant berhasil dibuat',
  'notify.merchant.create.fail': 'Gagal membuat merchant',
  'notify.merchant.save.ok': 'Merchant berhasil disimpan',
  'notify.merchant.save.fail': 'Gagal menyimpan merchant',
  'notify.report.regen.ok': 'Laporan diantrekan untuk regenerate',
  'notify.report.regen.fail': 'Gagal mengantrekan regenerate laporan',
  'notify.merchant.suspend.ok': 'Merchant disuspend',
  'notify.merchant.suspend.fail': 'Gagal suspend merchant',
  'notify.merchant.unsuspend.ok': 'Merchant diaktifkan kembali',
  'notify.merchant.unsuspend.fail': 'Gagal mengaktifkan merchant',
  'notify.merchant.regenCreds.ok': 'Kredensial baru diterbitkan',
  'notify.merchant.regenCreds.fail': 'Gagal menerbitkan kredensial',
  'notify.disbursement.create.ok': 'Disbursement berhasil dibuat',
  'notify.disbursement.create.fail': 'Gagal membuat disbursement',
  'notify.webhook.resend.ok': 'Webhook dikirim ulang',
  'notify.webhook.resend.fail': 'Gagal mengirim ulang webhook',
  'notify.trx.status.ok': 'Status transaksi diperbarui',
  'notify.trx.status.fail': 'Gagal memperbarui status transaksi',

  // Manual status override control (deposit + disbursement detail)
  'trxStatus.selectPlaceholder': 'Ubah status ke…',
  'trxStatus.resendCallback': 'Kirim ulang callback ke merchant',
  'trxStatus.updateButton': 'Update status',
  'trxStatus.confirmTitle': 'Ubah status transaksi secara manual?',
  'trxStatus.confirmBody':
    'Status transaksi {trxId} akan diubah dari {from} menjadi {to}. Saldo wallet merchant disesuaikan mengikuti perubahan ini (sama seperti callback dari provider) dan aksi ini dicatat di audit log.',
  'trxStatus.confirmCallbackOn': 'Callback "{status}" akan dikirim ke URL webhook merchant.',
  'trxStatus.confirmCallbackNoEvent':
    'Callback tidak akan dikirim: tidak ada event callback untuk status pending.',
  'trxStatus.confirmCallbackOff': 'Callback ke merchant TIDAK dikirim.',
  'trxStatus.confirmYes': 'Ya, ubah status',
  'trxStatus.noticeChanged': 'Status diubah dari {from} menjadi {to}.',
  'trxStatus.noticeCallbackQueued':
    'Callback di-queue ke {targetUrl} (outbox {outboxId}); worker akan mengirimnya dalam beberapa detik.',
  'trxStatus.noticeCallbackSkippedPending':
    'Callback tidak dikirim: tidak ada event callback untuk status pending.',
  'trxStatus.noticeCallbackSkippedNoUrl':
    'Callback tidak dikirim: merchant belum mengatur URL webhook.',
  'trxStatus.noticeCallbackNotRequested': 'Callback ke merchant tidak dikirim (tidak dicentang).',
  'notify.invite.accept.ok': 'Akun berhasil dibuat',
  'notify.invite.accept.fail': 'Gagal menerima undangan',
  'notify.apidoc.request.ok': 'Permintaan terkirim',
  'notify.apidoc.request.fail': 'Permintaan gagal',
  'notify.balance.adjustTitle': 'Saldo disesuaikan',
  'notify.balance.adjustOk': 'Saldo aktif sekarang {active}.',
  'notify.balance.adjustCarry':
    'Saldo aktif sekarang {active}. {carried} dialihkan ke settlement berikutnya (outstanding {outstanding}).',

  // ===== depositDetail =====
  'depositDetail.title': 'Detail Deposit',
  'depositDetail.resendTooltipReady': 'Re-queue ulang webhook ke merchant. Delivery ID baru, payload sama.',
  'depositDetail.resendTooltipNotReady': 'Belum ada webhook ke merchant — deposit belum paid/failed.',
  'depositDetail.resendButton': 'Kirim ulang webhook',
  'depositDetail.resendNotice':
    'Webhook di-re-queue ke {targetUrl}. Delivery ID baru: {deliveryId}. Worker akan publish ke SQS dalam ~beberapa detik; refresh API Activity untuk melihat hasilnya.',
  'depositDetail.fieldReference': 'Reference',
  'depositDetail.fieldMerchant': 'Merchant',
  'depositDetail.fieldMethod': 'Metode',
  'depositDetail.fieldFee': 'Fee',
  'depositDetail.fieldMerchantRevenue': 'Pendapatan Merchant',
  'depositDetail.fieldFromIp': 'Dari IP',
  'depositDetail.fieldPayer': 'Pembayar',
  'depositDetail.fieldRrn': 'RRN / Voucher',
  'depositDetail.fieldCreated': 'Dibuat (GMT+7)',
  'depositDetail.fieldPaid': 'Dibayar (GMT+7)',
  'depositDetail.fieldSettled': 'Settled (GMT+7)',
  'depositDetail.fieldScheduledSettlement': 'Jadwal Settlement (GMT+7)',
  'depositDetail.badgeSettled': 'settled',
  'depositDetail.badgeAwaiting': 'menunggu',
  'depositDetail.fieldRemark': 'Remark',

  // ===== disbursementDetail =====
  'disbursementDetail.title': 'Detail Disbursement',
  'disbursementDetail.resendTooltipReady': 'Re-queue ulang webhook ke merchant. Delivery ID baru, payload sama.',
  'disbursementDetail.resendTooltipNotReady': 'Belum ada webhook ke merchant — disbursement belum paid/failed.',
  'disbursementDetail.resendButton': 'Kirim ulang webhook',
  'disbursementDetail.resendNotice':
    'Webhook di-re-queue ke {targetUrl}. Delivery ID baru: {deliveryId}.',
  'disbursementDetail.fieldReference': 'Reference',
  'disbursementDetail.fieldMerchant': 'Merchant',
  'disbursementDetail.fieldMethod': 'Metode',
  'disbursementDetail.fieldBank': 'Bank',
  'disbursementDetail.fieldAccountNumber': 'Nomor rekening',
  'disbursementDetail.fieldHolderName': 'Nama pemilik',
  'disbursementDetail.fieldFee': 'Fee',
  'disbursementDetail.fieldTotalDebit': 'Total debit',
  'disbursementDetail.fieldRrn': 'RRN / Voucher',
  'disbursementDetail.fieldCreated': 'Dibuat (GMT+7)',
  'disbursementDetail.fieldPaid': 'Dibayar (GMT+7)',
  'disbursementDetail.fieldCreatedBy': 'Dibuat oleh',
  'disbursementDetail.fieldRemark': 'Remark',
  'disbursementDetail.upstreamProvider': 'Upstream provider',
  'disbursementDetail.fieldMerchantOrderId': 'Merchant Order ID',
  'disbursementDetail.fieldUpstreamOrderId': 'ID Order Upstream',
  'disbursementDetail.fieldInquiryToken': 'Inquiry Token',

  // ===== apiActivity (shared by deposit & disbursement detail) =====
  'apiActivity.title': 'API Activity',
  'apiActivity.descInternalDeposit':
    '<b>Initial Request</b> menampilkan request pertama dari merchant dan order create ke upstream provider. <b>Notification Cycles</b> mencatat setiap upstream callback yang masuk dan semua webhook delivery ke merchant yang mengikuti callback tersebut. Tiap row menampilkan response time-nya.',
  'apiActivity.descExternalDeposit':
    '<b>Initial Request</b> menampilkan request cashin dari merchant ke TransPay. <b>Webhook Notifications</b> mencatat setiap notifikasi yang TransPay kirim ke endpoint webhook merchant; satu notifikasi bisa memiliki beberapa attempt jika delivery sebelumnya gagal. Tiap row menampilkan response time-nya.',
  'apiActivity.descInternalDisbursement':
    '<b>Initial Request</b> menampilkan inquiry + order create dari merchant ke TransPay dan dari TransPay ke upstream provider. <b>Notification Cycles</b> mencatat setiap upstream callback dan webhook delivery yang mengikutinya. Tiap row tampilkan response time.',
  'apiActivity.descExternalDisbursement':
    '<b>Initial Request</b> menampilkan request dari Anda ke TransPay. <b>Webhook Notifications</b> mencatat notifikasi yang TransPay kirim ke endpoint webhook merchant.',
  'apiActivity.reloadTooltip': 'Reload',
  'apiActivity.refresh': 'Refresh',
  'apiActivity.upstreamMerchantOrderId': 'upstream merchantOrderId',
  'apiActivity.upstreamPlatformOrderId': 'upstream platformOrderId',
  'apiActivity.trxId': 'trxId',
  'apiActivity.merchantRowsVisible': 'Row merchant terlihat',
  'apiActivity.merchantRowsHidden': 'Row merchant disembunyikan',
  'apiActivity.upstreamRowsVisible': 'Row upstream provider terlihat',
  'apiActivity.reconstructedTooltip':
    'Beberapa row direkonstruksi dari penyimpanan legacy karena transaksi ini lebih tua daripada audit instrumentation. Field URL & duration mungkin tidak lengkap.',
  'apiActivity.reconstructedBadge': 'Direkonstruksi dari penyimpanan legacy',
  'apiActivity.entryCount': '· {n} entri',
  'apiActivity.loadFailed': 'Gagal memuat timeline',
  'apiActivity.emptyDeposit': 'Belum ada audit entry untuk transaksi ini.',
  'apiActivity.emptyDisbursement': 'Belum ada API activity untuk trx ini.',
  'apiActivity.notificationCycles': 'Notification Cycles',
  'apiActivity.webhookNotifications': 'Webhook Notifications',
  'apiActivity.orphanEntries': 'Orphan entries',
  'apiActivity.initialRequest': 'Initial Request',
  'apiActivity.initialSubtitleInternalDeposit': 'Cashin call dari merchant + order create ke upstream provider',
  'apiActivity.initialSubtitleExternalDeposit': 'Cashin call dari merchant ke TransPay',
  'apiActivity.initialSubtitleInternalDisbursement': '{inbound} call dari merchant + {outbound} call ke upstream provider',
  'apiActivity.initialSubtitleExternalDisbursement': '{inbound} call dari Anda ke TransPay',
  'apiActivity.labelMerchantApiRequest': 'Merchant → TransPay (API Request)',
  'apiActivity.labelMerchantApiRequestNoun': 'Permintaan API Merchant',
  'apiActivity.labelUpstreamOrderCreate': 'TransPay → Upstream Provider (Order Create)',
  'apiActivity.labelUpstreamOrderCreateNoun': 'Order Create Upstream',
  'apiActivity.labelMerchantInquiry': 'Merchant → TransPay (Account Inquiry)',
  'apiActivity.labelMerchantOrderCreate': 'Merchant → TransPay (Order Create)',
  'apiActivity.labelMerchantToTranspay': 'Merchant → TransPay',
  'apiActivity.labelPayokInquiry': 'TransPay → PAYOK (Account Inquiry)',
  'apiActivity.labelPayokOrderCreate': 'TransPay → PAYOK (Order Create)',
  'apiActivity.labelPayokOrderQuery': 'TransPay → PAYOK (Order Query)',
  'apiActivity.labelTranspayToPayok': 'TransPay → PAYOK',
  'apiActivity.labelPayokCallback': 'PAYOK → TransPay (Callback received)',
  'apiActivity.labelMerchantWebhook': 'TransPay → Merchant (Webhook attempt)',
  'apiActivity.cycleTitle': 'Cycle #{n}',
  'apiActivity.notificationTitle': 'Notification #{n}',
  'apiActivity.cycleSubtitleOne': '1 upstream callback + {n} merchant webhook attempt',
  'apiActivity.cycleSubtitleMany': '1 upstream callback + {n} merchant webhook attempts',
  'apiActivity.notificationSubtitleOne': '{n} delivery attempt ke webhook endpoint merchant',
  'apiActivity.notificationSubtitleMany': '{n} delivery attempts ke webhook endpoint merchant',
  'apiActivity.summaryErrors': 'errors',
  'apiActivity.summaryWorstHttp': 'worst HTTP {n}',
  'apiActivity.cycleEntries': '{n} entries',
  'apiActivity.upstreamCallbackNoun': 'Upstream Callback',
  'apiActivity.upstreamCallbackLabel': 'Upstream Provider → TransPay (Callback received)',
  'apiActivity.webhookAttemptNoun': 'Webhook Attempt #{n}',
  'apiActivity.webhookAttemptLabel': 'TransPay → Merchant (Webhook attempt #{n})',
  'apiActivity.entryNoun': 'entry',
  'apiActivity.legacyTooltipDeposit': 'Direkonstruksi dari kolom legacy',
  'apiActivity.legacyBadge': 'legacy',
  'apiActivity.legacyTooltipDisbursement':
    'Direkonstruksi dari disbursements row (audit log row hilang — trx pre-instrumentation atau dibuat via script).',
  'apiActivity.reconstructedRowBadge': 'direkonstruksi',
  'apiActivity.durationTooltipInbound': 'Waktu TransPay merespon panggilan inbound ini',
  'apiActivity.durationTooltipOutbound': 'Waktu pihak remote merespon panggilan outbound kita',
  'apiActivity.noTiming': 'tanpa timing',
  'apiActivity.statusErr': 'ERR',
  'apiActivity.started': 'Dimulai',
  'apiActivity.completed': 'Selesai',
  'apiActivity.responseTime': 'Response Time',
  'apiActivity.request': 'Request',
  'apiActivity.response': 'Response',
  'apiActivity.headers': 'Headers',
  'apiActivity.body': 'Body',

  // ===== disbursements (create wizard + extras) =====
  'disbursements.create': 'Buat disbursement',
  'disbursements.mfaBannerTitle': 'MFA belum aktif',
  'disbursements.mfaBannerBody':
    'Anda <strong>tidak bisa membuat disbursement</strong> sampai MFA aktif. Setiap submission membutuhkan kode 6-digit dari authenticator app. Enroll authenticator (Google Authenticator / Authy / 1Password / Bitwarden) sekarang — sekali setup, kira-kira 1 menit.',
  'disbursements.mfaEnrollNow': 'Enroll sekarang',
  'disbursements.wizardTitleForm': 'Buat Disbursement',
  'disbursements.wizardTitleConfirm': 'Konfirmasi',
  'disbursements.wizardStep1': 'Step 1 dari 2 — validasi rekening tujuan ke bank penerima. Belum memotong wallet.',
  'disbursements.wizardBankLabel': 'Bank tujuan',
  'disbursements.wizardBankLoading': 'memuat…',
  'disbursements.wizardBankPlaceholder': 'pilih bank',
  'disbursements.wizardAccountNumberLabel': 'Nomor rekening',
  'disbursements.wizardAccountNumberPlaceholder': '1234567890',
  'disbursements.wizardHolderNameLabel': 'Nama pemilik rekening',
  'disbursements.wizardHolderNamePlaceholder': 'Sesuai buku tabungan',
  'disbursements.wizardAmountLabel': 'Nominal (IDR)',
  'disbursements.wizardAmountPlaceholder': '50000',
  'disbursements.wizardRemarkLabel': 'Remark / deskripsi (opsional)',
  'disbursements.wizardRemarkPlaceholder': 'Withdrawal #1',
  'disbursements.wizardCancel': 'Batal',
  'disbursements.wizardValidateAccount': 'Validasi rekening',
  'disbursements.wizardConfirmAlert':
    'Rekening berhasil divalidasi ke bank penerima. Periksa nama pemilik dan total pendebetan di bawah sebelum klik Submit. Submit memotong active balance senilai amount + fee.',
  'disbursements.wizardFieldBank': 'Bank',
  'disbursements.wizardFieldAccountNumber': 'Nomor rekening',
  'disbursements.wizardFieldVerifiedHolder': 'Nama pemilik (terkonfirmasi bank)',
  'disbursements.wizardFieldAmount': 'Amount',
  'disbursements.wizardFieldFee': 'TransPay fee',
  'disbursements.wizardFieldTotalDebit': 'Total debit dari active',
  'disbursements.wizardFieldInquiryToken': 'Inquiry token (audit)',
  'disbursements.wizardPasswordLabel': 'Verifikasi password Anda',
  'disbursements.wizardPasswordDesc': 'Step-up auth: password Anda diperlukan untuk submit disbursement. Tindakan dicatat di audit log.',
  'disbursements.wizardMfaLabel': 'Kode MFA (dari authenticator app)',
  'disbursements.wizardMfaPlaceholder': '123456 atau XXXX-XXXX',
  'disbursements.wizardMfaDesc': '6-digit code dari Google Authenticator / Authy / 1Password / Bitwarden, ATAU backup code (XXXX-XXXX). Wajib untuk setiap disbursement.',
  'disbursements.wizardBackEdit': 'Kembali edit',
  'disbursements.wizardSubmit': 'Submit disbursement',
  'disbursements.errInvalidPassword': 'Password salah. Coba lagi.',
  'disbursements.errInvalidMfa': 'Kode MFA tidak valid atau sudah expired. Cek authenticator app.',

  // ===== auditLog =====
  'auditLog.scopeInternal': 'Staff TransPay',
  'auditLog.scopeExternal': 'Merchant',
  'auditLog.scopeSystem': 'Sistem',
  'auditLog.actLoginSuccessLabel': 'Login berhasil',
  'auditLog.actLoginSuccessNarrative': 'Berhasil masuk ke dashboard.',
  'auditLog.actLoginFailedLabel': 'Login gagal',
  'auditLog.actLoginFailedNarrative': 'Gagal login — {reason}',
  'auditLog.reasonWrongPassword': 'Password salah.',
  'auditLog.reasonUnknownEmail': 'Email tidak terdaftar.',
  'auditLog.reasonIpNotWhitelisted': 'IP tidak masuk dalam daftar whitelist.',
  'auditLog.reasonIpWhitelistNotConfigured': 'Akun belum memiliki IP whitelist.',
  'auditLog.reasonIpUnknown': 'IP pengguna tidak terdeteksi.',
  'auditLog.actLogoutLabel': 'Logout',
  'auditLog.actLogoutNarrative': 'Keluar dari dashboard.',
  'auditLog.actCashinCreateLabel': 'Membuat transaksi deposit',
  'auditLog.actCashinCreateNarrative': 'Deposit {amount} via {method} dibuat.',
  'auditLog.actDisbursementInquiryLabel': 'Cek rekening tujuan',
  'auditLog.actDisbursementInquiryNarrative': 'Validasi rekening {accountNumber} ({bank}) untuk disbursement {amount}.',
  'auditLog.actDisbursementCreateLabel': 'Mengirim uang ke rekening',
  'auditLog.actDisbursementCreateNarrative': 'Disbursement {amount} ke {holderName} di {bank} ({accountNumber}).',
  'auditLog.actMerchantCreateLabel': 'Membuat merchant baru',
  'auditLog.actMerchantCreateNarrative': 'Merchant "{name}" dibuat.',
  'auditLog.actMerchantUpdateLabel': 'Mengubah data merchant',
  'auditLog.actMerchantUpdateNarrative': 'Pengaturan merchant diperbarui.',
  'auditLog.actMerchantRevealLabel': 'Melihat API token merchant',
  'auditLog.actMerchantRevealNarrative': 'Mengambil API token plaintext (sensitif).',
  'auditLog.actMerchantRegenLabel': 'Membuat ulang API token',
  'auditLog.actMerchantRegenNarrative': 'API token diregenerate. Suffix baru: {newSuffix}.',
  'auditLog.actMerchantIpAddLabel': 'Menambah IP whitelist merchant',
  'auditLog.actMerchantIpAddNarrative': 'IP {cidr} ditambahkan ke whitelist API merchant.',
  'auditLog.actUserIpAddLabel': 'Menambah IP whitelist user',
  'auditLog.actUserIpAddNarrative': 'IP {cidr} ditambahkan ke whitelist login user.',
  'auditLog.actUsersCreateLabel': 'Membuat user baru',
  'auditLog.actUsersCreateNarrative': 'User baru: {email}.',
  'auditLog.actRolesUpdateLabel': 'Mengubah permission role',
  'auditLog.actRolesUpdateNarrative': 'Permission role "{roleSlug}" diperbarui.',
  'auditLog.actWebhookDepositResendLabel': 'Kirim ulang webhook deposit',
  'auditLog.actWebhookDepositResendNarrative': 'Webhook deposit dikirim ulang ke merchant.',
  'auditLog.actWebhookDisbursementResendLabel': 'Kirim ulang webhook disbursement',
  'auditLog.actWebhookDisbursementResendNarrative': 'Webhook disbursement dikirim ulang ke merchant.',
  'auditLog.actSettingsUpdateLabel': 'Mengubah pengaturan',
  'auditLog.actSettingsUpdateNarrative': 'Pengaturan "{key}" diperbarui.',
  'auditLog.actSettingsResetLabel': 'Mereset pengaturan',
  'auditLog.actSettingsResetNarrative': 'Pengaturan "{key}" dikembalikan ke default.',
  'auditLog.noExtraDetail': 'Tidak ada detail tambahan.',
  'auditLog.fieldAmount': 'Jumlah',
  'auditLog.fieldFee': 'Fee TransPay',
  'auditLog.fieldTotalDebit': 'Total Pemotongan',
  'auditLog.fieldMerchantRevenue': 'Pendapatan Merchant',
  'auditLog.fieldBankCode': 'Kode Bank',
  'auditLog.fieldBankName': 'Nama Bank',
  'auditLog.fieldAccountNumber': 'Nomor Rekening',
  'auditLog.fieldHolderName': 'Nama Pemilik',
  'auditLog.fieldVerifiedHolderName': 'Nama Pemilik (PAYOK)',
  'auditLog.fieldMerchantId': 'Merchant ID',
  'auditLog.fieldTrxId': 'Transaction ID',
  'auditLog.fieldReferenceId': 'Reference ID',
  'auditLog.fieldPayokPlatformOrderId': 'PAYOK Platform Order ID',
  'auditLog.fieldPayokMerchantOrderId': 'PAYOK Merchant Order ID',
  'auditLog.fieldUserId': 'User ID',
  'auditLog.fieldUserType': 'Tipe Pengguna',
  'auditLog.fieldIsSuperuser': 'Superuser',
  'auditLog.fieldReason': 'Penyebab',
  'auditLog.fieldMethod': 'Metode',
  'auditLog.fieldIp': 'IP',
  'auditLog.fieldCidr': 'CIDR',
  'auditLog.fieldNewSuffix': 'Suffix Token Baru',
  'auditLog.fieldKey': 'Setting Key',
  'auditLog.fieldEmail': 'Email',
  'auditLog.fieldName': 'Nama',
  'auditLog.fieldJti': 'JWT ID',
  'auditLog.fieldNewOutboxId': 'Outbox ID Baru',
  'auditLog.fieldNewDeliveryId': 'Delivery ID Baru',
  'auditLog.fieldSourceOutboxId': 'Outbox ID Sumber',
  'auditLog.fieldSourceStatus': 'Status Sumber',
  'auditLog.fieldTargetUrl': 'Target URL',
  'auditLog.fieldRemark': 'Catatan',
  'auditLog.boolYes': 'Ya',
  'auditLog.boolNo': 'Tidak',
  'auditLog.accessDenied': 'Audit log hanya bisa diakses oleh internal user dengan permission audit.view.',
  'auditLog.title': 'Audit Log',
  'auditLog.subtitle':
    'Forensik untuk semua mutating action: login, credential, disbursement, cashin, webhook resend, settings. Internal-only.',
  'auditLog.refresh': 'Refresh',
  'auditLog.filter': 'Filter',
  'auditLog.activeCount': '{n} aktif',
  'auditLog.collapse': 'Tutup',
  'auditLog.expand': 'Buka',
  'auditLog.actionLabel': 'Action',
  'auditLog.actionPlaceholderLoading': 'memuat…',
  'auditLog.actionPlaceholder': 'pilih action',
  'auditLog.actorEmailLabel': 'Actor email (substring)',
  'auditLog.actorEmailPlaceholder': '@transpay.com',
  'auditLog.actorScopeLabel': 'Actor scope',
  'auditLog.actorScopePlaceholder': 'semua',
  'auditLog.actorScopeInternal': 'internal',
  'auditLog.actorScopeExternal': 'external (merchant)',
  'auditLog.actorScopeSystem': 'system',
  'auditLog.targetLabel': 'Target (substring)',
  'auditLog.targetPlaceholder': 'merchant:<uuid> / disbursement:<uuid> / dst',
  'auditLog.dateLabel': 'Tanggal (GMT+7)',
  'auditLog.datePlaceholder': 'pilih rentang tanggal',
  'auditLog.reset': 'Reset',
  'auditLog.apply': 'Terapkan',
  'auditLog.loadFailed': 'Gagal memuat',
  'auditLog.colTime': 'Waktu (GMT+7)',
  'auditLog.colActivity': 'Aktivitas',
  'auditLog.colActor': 'Oleh',
  'auditLog.colType': 'Tipe',
  'auditLog.empty': 'Tidak ada audit row yang cocok dengan filter.',
  'auditLog.ipUndetected': 'IP tidak terdeteksi',
  'auditLog.pageRows': 'Halaman {page} · {count} baris',
  'auditLog.previous': 'Sebelumnya',
  'auditLog.next': 'Selanjutnya',
  'auditLog.detailActivity': 'Detail Aktivitas',
  'auditLog.previousValue': 'Nilai Sebelumnya',
  'auditLog.target': 'Objek',
  'auditLog.auditId': 'Audit ID',
  'auditLog.requestId': 'Request ID',
  'auditLog.hideRawJson': 'Sembunyikan JSON mentah (untuk IT)',
  'auditLog.showRawJson': 'Tampilkan JSON mentah (untuk IT)',
  'auditLog.rawBefore': 'before',
  'auditLog.rawAfter': 'after',

  // ===== apiDocs =====
  'apiDocs.reauthIntro':
    'Halaman API Documentation berisi daftar endpoint internal TransPay. Untuk membukanya, mohon konfirmasi password akun Anda sekali lagi.',
  'apiDocs.accessDenied':
    'Anda tidak memiliki izin untuk membuka halaman Dokumentasi API. Hubungi admin Anda untuk meminta akses.',
  'apiDocs.title': 'API Documentation',
  'apiDocs.subtitle':
    'Daftar endpoint yang bisa Anda call. Disesuaikan dengan role + permission akun yang sedang login — endpoint yang bukan untuk Anda tidak ditampilkan.',
  'apiDocs.sessionInactive': 'Sesi belum aktif. Login dulu untuk melihat API docs.',
  'apiDocs.youAre': 'Anda adalah',
  'apiDocs.role': 'Role',
  'apiDocs.noRole': '(tanpa role)',
  'apiDocs.visibleEndpoints': 'Endpoint terlihat',
  'apiDocs.visibleOf': '{visible} dari {total}',
  'apiDocs.hiddenByPermissions': '{n} disembunyikan oleh permission',
  'apiDocs.permissionsHeld': 'Permission dimiliki',
  'apiDocs.allSuperuserBypass': 'SEMUA (bypass superuser)',
  'apiDocs.searchLabel': 'Cari',
  'apiDocs.searchPlaceholder': 'path, judul, deskripsi, atau permission key',
  'apiDocs.categoryLabel': 'Kategori',
  'apiDocs.categoryPlaceholder': 'semua kategori',
  'apiDocs.tabAll': 'Semua',
  'apiDocs.tabServerToServer': 'Server-to-Server',
  'apiDocs.tabDashboard': 'Dashboard',
  'apiDocs.noMatch': 'Tidak ada endpoint yang cocok dengan filter saat ini.',
  'apiDocs.badgeServerToServer': 'server-to-server',
  'apiDocs.badgeInternal': 'internal',
  'apiDocs.badgeMerchant': 'merchant',
  'apiDocs.baseUrl': 'Base URL',
  'apiDocs.baseUrlNote': 'Production. Untuk environment lain, ganti host-nya.',
  'apiDocs.permissionRequired': 'Permission diperlukan (SALAH SATU dari)',
  'apiDocs.requestHeaders': 'Request headers',
  'apiDocs.colName': 'Nama',
  'apiDocs.paymentMethods': 'Metode Pembayaran',
  'apiDocs.colCategory': 'Kategori',
  'apiDocs.colRequired': 'Wajib?',
  'apiDocs.colDescription': 'Deskripsi',
  'apiDocs.colExample': 'Contoh',
  'apiDocs.required': 'wajib',
  'apiDocs.optional': 'opsional',
  'apiDocs.curlExample': 'contoh curl',
  'apiDocs.requestBody': 'Request body',
  'apiDocs.responseSample': 'Response (sampel)',
  'apiDocs.responseFields': 'Response fields',
  'apiDocs.colPath': 'Path',
  'apiDocs.colType': 'Tipe',
  'apiDocs.responseHeaders': 'Response headers',
  'apiDocs.errorCodes': 'Error codes',
  'apiDocs.colCode': 'Code',
  'apiDocs.colHttp': 'HTTP',
  'apiDocs.fullPath': 'Full path:',

  // ===== merchantCreate =====
  'merchantCreate.internalOnly': 'Hanya internal user yang dapat membuat merchant.',
  'merchantCreate.title': 'Buat Merchant',
  'merchantCreate.create': 'Buat Merchant',
  'merchantCreate.basics': 'Dasar',
  'merchantCreate.nameLabel': 'Nama',
  'merchantCreate.emailLabel': 'Email',
  'merchantCreate.depositFeeLabel': 'Fee deposit (bps)',
  'merchantCreate.depositFeeDesc': '70 = 0,70 %',
  'merchantCreate.disbursementFeeLabel': 'Fee disbursement (IDR)',
  'merchantCreate.apiAccessLabel': 'API access aktif',
  'merchantCreate.valRequired': 'Wajib diisi',
  'merchantCreate.valInvalidEmail': 'Email tidak valid',
  'merchantCreate.webhookTitle': 'Webhook (Superuser only)',
  'merchantCreate.depositWebhookLabel': 'Deposit webhook URL',
  'merchantCreate.depositWebhookDesc':
    'Tempat TransPay mengirim notifikasi pembayaran. Superuser only.',
  'merchantCreate.payokTitle': 'PAYOK config (Superuser only)',
  'merchantCreate.payokEnable': 'Aktifkan',
  'merchantCreate.payokManagedNote': 'Konfigurasi koneksi PAYOK (base URL, MID, endpoint, kunci, URL notifikasi) dikelola terpusat di menu Acquirer. Saat membuat merchant, cukup aktif/nonaktifkan pembayaran di sini.',
  'merchantCreate.cancel': 'Batal',
  'merchantCreate.revealTitle': 'Merchant dibuat — simpan kredensial ini',
  'merchantCreate.revealWarning':
    'Plaintext token + webhook secret hanya ditampilkan SEKALI. Simpan sekarang sebelum menutup dialog. Setelah ditutup hanya 8-char suffix yang bisa dilihat.',
  'merchantCreate.fieldClientId': 'Transpay Client ID',
  'merchantCreate.fieldClientToken': 'Transpay Client Token',
  'merchantCreate.fieldWebhookSecret': 'Webhook HMAC Secret',
  'merchantCreate.revealFooterPre':
    'Setelah klik OK, Anda akan diarahkan ke halaman detail merchant. Tambahkan minimal 1 CIDR ke',
  'merchantCreate.revealFooterPost':
    'sebelum API cashin bisa dipakai (deny-all default).',

  // ===== acceptInvite =====
  'acceptInvite.title': 'Terima Undangan',
  'acceptInvite.tokenMissing':
    'token tidak ada — pastikan Anda membuka link undangan lengkap',
  'acceptInvite.invalidOrExpired': 'undangan tidak valid atau kedaluwarsa',
  'acceptInvite.invitedAs': 'Anda diundang sebagai:',
  'acceptInvite.noRole': 'Tidak ada role yang ditetapkan',
  'acceptInvite.invitedByExpires': 'Diundang oleh {invitedBy} · kedaluwarsa {expiresAt}',
  'acceptInvite.emailLabel': 'Email',
  'acceptInvite.nameLabel': 'Nama Anda',
  'acceptInvite.nameDesc': 'Bagaimana Anda ingin ditampilkan di dashboard',
  'acceptInvite.passwordLabel': 'Password',
  'acceptInvite.passwordDesc': 'min 12 karakter',
  'acceptInvite.confirmLabel': 'Konfirmasi password',
  'acceptInvite.submit': 'Terima & masuk',
  'acceptInvite.haveAccount': 'Sudah punya akun?',
  'acceptInvite.signIn': 'Masuk',
  'acceptInvite.valRequired': 'wajib diisi',
  'acceptInvite.valMinChars': 'min 12 karakter',
  'acceptInvite.valMismatch': 'tidak cocok dengan password',

  // ===== security =====
  'security.title': 'Security',
  'security.subtitle':
    'Pengaturan keamanan akun Anda. MFA wajib aktif sebelum bisa melakukan disbursement.',
  'security.statusLoadFailed': 'Gagal memuat status MFA',
  'security.mfaTitle': 'Multi-Factor Authentication (TOTP)',
  'security.badgeActive': 'Aktif',
  'security.badgeNotEnabled': 'Belum diaktifkan',
  'security.mfaHint':
    'Pakai authenticator app (Google Authenticator, Authy, 1Password, Bitwarden, dll)',
  'security.disableMfa': 'Disable MFA',
  'security.enrollAuthenticator': 'Enroll authenticator',
  'security.labelEnrolled': 'Enrolled',
  'security.labelLastUsed': 'Last used',
  'security.labelBackupCodesLeft': 'Backup codes left',
  'security.backupCodesLowWarning':
    'Backup codes hampir habis. Kalau perangkat authenticator hilang, Anda harus minta admin untuk reset MFA. Pertimbangkan untuk disable + enroll ulang supaya dapat set backup codes baru.',
  'security.enrollErrInit': 'Enroll init gagal',
  'security.modalTitleSaveCodes': 'Simpan backup codes Anda',
  'security.modalTitleEnroll': 'Enroll authenticator app',
  'security.generatingSecret': 'Generating secret…',
  'security.enrollStep1':
    '1. Buka authenticator app Anda (Google Authenticator, Authy, 1Password, Bitwarden, dll). Scan QR di bawah <strong>atau</strong> ketik manual secret.',
  'security.qrAlt': 'MFA QR code',
  'security.secretManual': 'Secret manual:',
  'security.enrollStep2':
    '2. Setelah secret tersimpan di app, masukkan 6-digit code yang muncul:',
  'security.verifyActivate': 'Verifikasi & aktifkan',
  'security.saveCodesTitle': 'Simpan 10 backup codes ini SEKARANG.',
  'security.saveCodesBody':
    'Setiap kode bisa dipakai SEKALI untuk submit disbursement kalau Anda kehilangan perangkat authenticator. Tidak akan ditampilkan lagi. Simpan di password manager, atau cetak dan letakkan di lokasi aman.',
  'security.copiedAll10': 'Tersalin semua 10',
  'security.copyAll': 'Copy semua',
  'security.savedClose': 'Saya sudah simpan, tutup',
  'security.retry': 'Coba lagi',
  'security.disableMfaTitle': 'Disable MFA',
  'security.disableWarningPre': 'Setelah MFA dimatikan, Anda',
  'security.disableWarningBold': 'tidak bisa melakukan disbursement',
  'security.disableWarningPost': 'sampai MFA aktif kembali. Pastikan ini memang yang Anda mau.',
  'security.accountPasswordLabel': 'Password akun',
  'security.mfaCodeLabel': 'MFA code (dari authenticator)',
} as const;

const en: Record<keyof typeof id, string> = {
  // ===== common =====
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.reset': 'Reset',
  'table.resetWidths': 'Reset column widths',
  'table.resizeColumn': 'Resize column (drag, or left/right arrow keys; Home resets)',
  'table.columns': 'Columns',
  'table.showAll': 'Show all',
  'table.scrollLeft': 'Scroll table left',
  'table.scrollRight': 'Scroll table right',
  'pagination.summary': '{total} rows · page {page} of {pages}',
  'pagination.perPage': 'per page',
  'listSummary.title': 'Filtered data overview',
  'listSummary.filteredHint': 'Follows the filters currently applied (all statuses)',
  'listSummary.loadFailed': 'Failed to load the overview',
  'listSummary.total': 'Total transactions',
  'listSummary.amount': 'Total amount',
  'listSummary.fee': 'Total fee',
  'listSummary.revenue': 'Total merchant revenue',
  'listSummary.byStatus': 'By status',
  'listSummary.byMethod': 'By method',
  'listSummary.byBank': 'By bank',
  'listSummary.count': 'trx',
  'listSummary.groups': '{n} groups',
  'listSummary.showAll': 'Show all ({n})',
  'listSummary.showTop': 'Show top {n}',
  'list.toggleOverview': 'Show / hide the filtered data overview',
  'list.toggleFilters': 'Show / hide filters',
  'json.copy': 'Copy JSON',
  'json.copied': 'Copied',
  'json.viewCode': 'JSON',
  'json.viewTable': 'Table',
  'json.empty': 'empty',
  'json.items': '{n} items',
  'json.keys': '{n} keys',
  'common.refresh': 'Refresh',
  'common.reload': 'Reload',
  'common.filters': 'Filters',
  'common.apply': 'Apply',
  'common.delete': 'Delete',
  'common.create': 'Create',
  'common.add': 'Add',
  'common.back': 'Back',
  'common.error': 'Error',
  'common.unknown': 'Unknown',
  'common.collapse': 'Collapse',
  'common.show': 'Show',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.prev': 'Prev',

  // ===== auth =====
  'auth.superuser': 'Superuser',
  'auth.internal': 'Internal',
  'auth.merchant': 'Merchant',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.captcha': 'Captcha',
  'auth.logout': 'Logout',
  'auth.loginTitle': 'TransPay Dashboard',
  'auth.confirmPassword': 'Confirm your password',
  'auth.unlock': 'Unlock',
  'auth.loginTagline': 'Sign in to view balances, transactions, and manage merchants.',
  'auth.emailPlaceholder': 'user_merchant@trans-pay.app',
  'auth.passwordPlaceholder': '••••••••',
  'auth.captchaPlaceholder': 'Type the code on the right',
  'auth.captchaCurrent': 'Current code: {code}',
  'auth.refreshCaptcha': 'Refresh captcha',
  'auth.signIn': 'Sign In',
  'auth.needAccess': 'Need access?',
  'auth.contactAdmin': 'Contact your admin',
  'auth.errInvalidEmail': 'Invalid email',
  'auth.errPasswordTooShort': 'Min 8 characters',
  'auth.errIpWhitelistNotConfigured':
    'Your account has no IP whitelist configured. Ask an admin to add your IP.',
  'auth.errIpNotWhitelisted':
    'Your IP is not on the whitelist. Sign in from an allowed location, or ask an admin to add your IP.',
  'auth.errInvalidCredentials': 'Wrong email or password.',
  'auth.errCaptchaMismatch': 'Captcha code does not match.',
  'auth.errConcurrentLogin':
    'Another active session was detected for this account. All sessions were ended for security — please log in again.',
  'auth.errMfaInvalid': 'MFA code is incorrect or expired.',
  'auth.errAccountLocked': 'Too many failed attempts. Account temporarily locked; try again later.',
  'auth.errInvalidCredentialsWithCount':
    'Invalid email or password. Attempt {count} of {max}.',
  'auth.errIpNotWhitelistedWithCount':
    'IP {ip} is not in your whitelist. Attempt {count} of {max}.',
  'auth.errMfaInvalidWithCount': 'MFA code incorrect or expired. Attempt {count} of {max}.',
  'auth.errAccountSuspendedNow':
    'Your account has been suspended after {max} failed login attempts. Contact an administrator to restore access.',
  'auth.errAccountSuspended':
    'Your account is suspended. Contact an administrator to restore access.',
  'auth.errMerchantSuspendedNow':
    'Because you are a merchant admin, your merchant and every user under it have also been suspended.',
  'auth.errMerchantSuspended':
    'Your merchant is suspended. Contact an administrator to restore access.',
  'auth.mfaCodeLabel': 'MFA code',
  'auth.mfaModalTitle': 'Two-Factor Verification (2FA)',
  'auth.mfaModalIntro':
    'Your email & password are correct. This account is protected by MFA — open your authenticator app (e.g. Google Authenticator / Authy) on your phone and enter the 6-digit code to continue.',
  'auth.mfaModalVerify': 'Verify & Sign In',
  'auth.sessionExpiredTitle': 'Session expired',
  'auth.sessionExpired': 'Your session has expired. Please sign in again.',
  'auth.sessionKilled.superseded.title': 'Signed in on another device',
  'auth.sessionKilled.superseded.body':
    'This session was ended because your account signed in on another browser or device. Please sign in again.',
  'auth.sessionKilled.ipChanged.title': 'Network address changed',
  'auth.sessionKilled.ipChanged.body':
    'Your IP address changed, so for security this session was ended. Please sign in again.',
  'auth.sessionKilled.concurrentTab.title': 'Opened in another tab',
  'auth.sessionKilled.concurrentTab.body':
    'TransPay was opened in another tab or window. For security, this session was ended. Please sign in again.',
  'auth.sessionKilled.loggedOut.title': 'Session ended',
  'auth.sessionKilled.loggedOut.body': 'Your session was ended. Please sign in again.',
  'auth.sessionKilled.suspended.title': 'Account suspended',
  'auth.sessionKilled.suspended.body': 'Your account has been suspended. Contact your administrator.',
  'auth.sessionKilled.button': 'Sign in again',

  // ===== nav =====
  'nav.overview': 'Overview',
  'nav.deposits': 'Deposits',
  'nav.disbursements': 'Disbursements',
  'nav.merchants': 'Merchants',
  'nav.wallet': 'Wallet',
  'nav.apiCredentials': 'API Credentials',
  'nav.users': 'Users',
  'nav.roles': 'Roles & Permissions',
  'nav.apiDocs': 'API Documentation',
  'nav.security': 'Security',
  'nav.settings': 'Platform Settings',
  'nav.acquirers': 'Acquirers',
  'nav.auditLog': 'Audit Log',
  'nav.transactionReports': 'Transaction Reports',
  'nav.transactionSummary': 'Transaction Summary',
  'nav.settlementReports': 'Settlement Reports',

  // ===== summary — on-screen aggregated transaction summary =====
  'summary.title': 'Transaction Summary',
  'summary.subtitle': 'Aggregated transaction summary by method & bank for a date range',
  'summary.dateRange': 'Date range',
  'summary.kpiCashIn': 'Total Cash In',
  'summary.kpiCashOut': 'Total Cash Out',
  'summary.kpiFee': 'Total Fee',
  'summary.kpiFeeHint': 'deposit + disbursement',
  'summary.kpiTrxPaid': '{count} paid trx',
  'summary.cashInDetail': 'Cash In Detail',
  'summary.cashOutDetail': 'Cash Out Detail',
  'summary.colProduct': 'Product / Method',
  'summary.colTrxCount': 'Trx Count',
  'summary.colGross': 'Gross income',
  'summary.colFee': 'Fee',
  'summary.searchPlaceholder': 'Search…',
  'summary.empty': 'No transactions in this range',
  'summary.presetToday': 'Today',
  'summary.presetYesterday': 'Yesterday',
  'summary.presetLast7': 'Last 7 Days',
  'summary.presetLast30': 'Last 30 Days',
  'summary.presetThisMonth': 'This Month',
  'summary.presetLastMonth': 'Last Month',
  'summary.presetThisYear': 'This Year',
  'summary.presetLastYear': 'Last Year',

  // ===== settlement — daily settlement report (same pipeline as reports.*) =====
  'settlement.title': 'Settlement Reports',
  'settlement.subtitleMerchant':
    'Daily report files of your merchant\'s settled deposits (Pending → Active), generated automatically every morning.',
  'settlement.subtitleInternal':
    'Daily settlement reports per merchant. Leave merchant filter empty to view all merchants (requires view.all permission).',
  'settlement.scheduleInfo':
    'Period = SETTLEMENT date (GMT+7), not transaction date: a deposit is reported on the day its funds moved from Pending to Active — the same Settlement rows listed under Balance Mutation on the Wallet page. ' +
    'Paid deposits still waiting for their settlement time are not included yet. Files are generated automatically at 04:00 WIB for the previous day\'s settlements.',
  'settlement.colPeriod': 'Settlement date',
  'settlement.colRows': 'Trx settled',
  'settlement.colNet': 'Net settled',
  'settlement.grossFeeHint': 'gross {gross} · fee {fee}',
  'settlement.empty': 'No settlement reports match this filter.',
  'settlement.noTransactions': 'No settlements',
  'settlement.noTransactionsDetail':
    'None of this merchant\'s deposits settled on this date, so no report file was generated (saves server storage).',
  'settlement.detailTitle': 'Settlement Report Detail — {period}',
  'settlement.fieldGross': 'Total gross',
  'settlement.fieldFee': 'Total fee',
  'settlement.fieldNet': 'Net settled',

  'reports.title': 'Transaction Reports',
  'reports.subtitleMerchant':
    'Daily cashin + cashout transaction reports for your merchant, generated automatically every morning.',
  'reports.subtitleInternal':
    'Daily reports per merchant. Leave merchant filter empty to view all merchants (requires view.all permission).',
  'reports.scheduleInfo':
    'Files are generated automatically at 04:00 WIB for the previous day. ' +
    'After 4 days the system rechecks the data; if anything has changed, a new version appears.',
  'reports.fromDate': 'From date',
  'reports.toDate': 'To date',
  'reports.merchantFilter': 'Merchant',
  'reports.merchantAll': 'All merchants',
  'reports.statusFilter': 'Status',
  'reports.statusAll': 'All statuses',
  'reports.colPeriod': 'Period',
  'reports.colMerchant': 'Merchant',
  'reports.colStatus': 'Status',
  'reports.colVersion': 'Version',
  'reports.colRows': 'Trx count',
  'reports.colGenerated': 'Generated',
  'reports.colSize': 'Size',
  'reports.colReason': 'Reason',
  'reports.empty': 'No reports match this filter.',
  'reports.statusReady': 'Ready',
  'reports.statusPending': 'Pending',
  'reports.statusGenerating': 'Generating',
  'reports.statusRegenerating': 'Updating',
  'reports.statusFailed': 'Failed',
  'reports.statusNoData': 'No transactions',
  'reports.noTransactions': 'No transactions',
  'reports.noTransactionsDetail': 'This merchant had no transactions for this period, so no report file was generated (saves server storage).',
  'reports.updatedBadge': 'Updated',
  'reports.regeneratedTooltip': 'Updated {date} — reason: {reason}',
  'reports.reasonRecheckDrift': 'Data changed during recheck (late callbacks / status flips)',
  'reports.reasonManualAdmin': 'Manual regeneration by admin',
  'reports.pendingRowsHint': 'pending — may still change',
  'reports.download': 'Download',
  'reports.downloadTooltip': 'Download XLSX file',
  'reports.downloadDisabledTooltip':
    "File not yet available (not generated or you don't have download permission).",
  'reports.openDetail': 'View detail',
  'reports.detailTitle': 'Report Detail — {period}',
  'reports.fieldMerchant': 'Merchant',
  'reports.fieldStatus': 'Status',
  'reports.fieldVersion': 'Version',
  'reports.fieldGeneratedBy': 'Generated by',
  'reports.fieldGeneratedAt': 'Generated at',
  'reports.fieldLastChecked': 'Last rechecked',
  'reports.fieldRows': 'Row count',
  'reports.fieldHash': 'Data hash',
  'reports.regeneratedAlert':
    'This file was updated after the initial generation. Reason: {reason}.',
  'reports.versionHistory': 'Version history',
  'reports.regenerateBtn': 'Regenerate now',

  // ===== dashboard =====
  'dashboard.title': 'Overview',
  'dashboard.welcome': 'Welcome, {name}',
  'dashboard.scopeMerchant': 'Merchant scope',
  'dashboard.scopePlatform': 'Platform-wide (all merchants)',
  'dashboard.balanceOverview': 'Balance Overview',
  'dashboard.balanceOverviewHint': '— summed across all merchants',
  'dashboard.pendingBalance': 'Pending Balance',
  'dashboard.pendingBalanceHint': 'Awaiting settlement boundary',
  'dashboard.activeBalance': 'Active Balance',
  'dashboard.activeBalanceHint': 'Ready to disburse',
  'dashboard.todayTrxCount': 'Today Trx Count',
  'dashboard.todayTrxCountHint': 'Created today (GMT+7)',
  'dashboard.totalOverview': 'Total Overview',
  'dashboard.totalOverviewHint': '— lifetime, settled only',
  'dashboard.totalDeposit': 'Total Deposit',
  'dashboard.totalDepositHint': 'Sum of settled cashin amounts',
  'dashboard.totalCountDeposit': 'Total Count Deposit',
  'dashboard.totalCountDepositHint': 'Paid deposit transactions',
  'dashboard.totalDisbursement': 'Total Disbursement',
  'dashboard.totalDisbursementHint': 'Sum of settled cashout amounts',
  'dashboard.totalCountCashOut': 'Total Count Cash Out',
  'dashboard.totalCountCashOutHint': 'Paid disbursement transactions',
  'dashboard.charts': 'Charts',
  'dashboard.chartRangeLabel': '— {from} → {to} (last 30 days)',
  'dashboard.chartAmountByDay': 'Daily Amount — Deposit vs Disbursement',
  'dashboard.chartDepositCountByMethod': 'Daily Deposit Count — by Method',
  'dashboard.chartDisbursementCountByMethod': 'Daily Disbursement Count — by Method',
  'dashboard.chartEmptyDeposit': 'No deposit data in the selected window.',
  'dashboard.chartEmptyDisbursement':
    'No disbursement data in the selected window. (withdraw-svc not yet active)',
  'dashboard.balancesHidden': 'You do not have permission to view the balance overview.',
  'dashboard.totalsHidden': 'Totals are not visible to your role.',
  'dashboard.chartsNeedPermission': 'Charts require the `overview.charts.view` permission.',
  'dashboard.chartsFailedToLoad': 'Failed to load charts',
  'dashboard.overviewFailedToLoad': 'Failed to load overview',
  'dashboard.deposit': 'Deposit',
  'dashboard.disbursement': 'Disbursement',

  // ===== not found =====
  'notFound.message': 'Page not found.',
  'notFound.backToDashboard': 'Back to Dashboard',

  // ===== deposits =====
  'deposits.title': 'Deposits',
  'deposits.subtitle': 'Cash-in transactions',
  'deposits.filters': 'Filters',
  'deposits.activeFilters': '{n} active',
  'deposits.colTrxId': 'Trx ID',
  'deposits.colMerchantReff': 'Merchant Reff',
  'deposits.colMerchantName': 'Merchant Name',
  'deposits.colCreatedAt': 'Created At',
  'deposits.colCreatedBy': 'Created By',
  'deposits.colCreatedByPartial': 'merchant name (partial)',
  'deposits.colRequestedFrom': 'Requested From',
  'deposits.colMethod': 'Method',
  'deposits.colStatus': 'Status',
  'deposits.colPaidAt': 'Paid At',
  'deposits.colSettleDate': 'Estimated Settle',
  'deposits.colSettlement': 'Actual Settle',
  'deposits.colAmount': 'Amount',
  'deposits.colFee': 'Fee',
  'deposits.colMerchantRevenue': 'Merchant Revenue',
  'deposits.placeholderTrxId': 'exact UUID',
  'deposits.placeholderReff': 'REF-...',
  'deposits.placeholderIp': '203.0.113.42',
  'deposits.placeholderAllMerchants': 'all merchants',
  'deposits.placeholderAllMethods': 'all methods',
  'deposits.placeholderAllStatuses': 'all statuses',
  'deposits.placeholderDateRange': 'pick a date range',
  'deposits.placeholderMin': 'min',
  'deposits.placeholderMax': 'max',
  'deposits.placeholderLoading': 'loading…',
  'deposits.createdAtLabel': 'Created At (GMT+7)',
  'deposits.paidAtLabel': 'Paid At (GMT+7)',
  'deposits.settleAtLabel': 'Settlement Date (GMT+7)',
  'deposits.amountLabel': 'Amount (IDR)',
  'deposits.feeLabel': 'Fee (IDR)',
  'deposits.merchantRevenueLabel': 'Merchant Revenue (IDR)',
  'deposits.empty': 'No transactions yet.',
  'deposits.settlementSettled': 'settled',
  'deposits.settlementAwaiting': 'awaiting',

  // ===== disbursements (EN) =====
  'disbursements.title': 'Disbursements',
  'disbursements.subtitle': 'Cash-out via upstream remit. Wallet flow: active → reserved at create, reserved → 0 (settled) on callback success, reserved → active on failure.',
  'disbursements.filters': 'Filters',
  'disbursements.activeFilters': '{n} active',
  'disbursements.colTrxId': 'Trx ID',
  'disbursements.colMerchantReff': 'Merchant Reff',
  'disbursements.colMerchantName': 'Merchant',
  'disbursements.colCreatedAt': 'Created',
  'disbursements.colCreatedBy': 'Created By',
  'disbursements.colCreatedByPartial': 'merchant name (partial)',
  'disbursements.colRequestedFrom': 'From IP',
  'disbursements.colStatus': 'Status',
  'disbursements.colPaidAt': 'Paid At',
  'disbursements.colBank': 'Bank',
  'disbursements.colBeneficiary': 'Beneficiary',
  'disbursements.colAmount': 'Amount',
  'disbursements.colFee': 'Fee',
  'disbursements.colMerchantRevenue': 'Wallet Impact',
  'disbursements.placeholderTrxId': 'exact UUID',
  'disbursements.placeholderReff': 'WD-...',
  'disbursements.placeholderIp': '203.0.113.42',
  'disbursements.placeholderHolder': 'holder name (partial)',
  'disbursements.placeholderAllMerchants': 'all merchants',
  'disbursements.placeholderAllBanks': 'all banks',
  'disbursements.placeholderAllStatuses': 'all statuses',
  'disbursements.placeholderDateRange': 'pick date range',
  'disbursements.placeholderMin': 'min',
  'disbursements.placeholderMax': 'max',
  'disbursements.createdAtLabel': 'Created At (GMT+7)',
  'disbursements.paidAtLabel': 'Paid At (GMT+7)',
  'disbursements.amountLabel': 'Amount (IDR)',
  'disbursements.feeLabel': 'Fee (IDR)',
  'disbursements.empty': 'No disbursements yet.',

  // ===== wallet =====
  'wallet.title': 'Wallet',
  'wallet.subtitle': 'Real-time balance. Auto-refreshes every 5 seconds.',
  'wallet.merchantIdLabel': 'merchant id {id}',
  'wallet.merchantOnlyHint':
    'The wallet page is only for merchant users. Internal users can open a wallet from the merchant detail page.',
  'wallet.internalTitle': 'Wallet — All Merchants',
  'wallet.internalSubtitle':
    'Aggregate balances across every merchant + cross-merchant balance mutations. Auto-refreshes every 5 seconds.',
  'wallet.internalMerchantCount': '{count} merchants',
  'wallet.internalAccessDenied': "You don't have permission to view all-merchant wallets.",
  'wallet.ledgerColMerchant': 'Merchant',
  'wallet.settlementTimingTitle': 'Settlement timing — twice per day',
  'wallet.settlementTimingIntro':
    'A paid deposit lands in Pending first, then auto-moves to Active once. By default:',
  'wallet.settlementBatchMorning':
    'Paid 12:00–23:59 GMT+7 → settles the next day at 13:00.',
  'wallet.settlementBatchEvening':
    'Paid 00:00–11:59 GMT+7 → settles the same day at 19:00.',
  'wallet.settlementBatchException':
    'Some payment methods (e.g. BCA VA) settle H+1 and skip weekends/holidays. The day and hour of both windows above are configurable by an admin in Platform Settings.',
  'wallet.settlementMeaning':
    'So Pending = "received, awaiting settlement", Active = "ready to disburse".',
  'wallet.balPending': 'Pending',
  'wallet.balActive': 'Active',
  'wallet.balReserved': 'Reserved',
  'wallet.balLifetimeDeposit': 'Lifetime Deposit',
  'wallet.balLifetimeDisbursement': 'Lifetime Disbursement',
  'wallet.balLifetimeFee': 'Lifetime Fee',
  'wallet.balPendingHint':
    'Not yet settled — includes paid trx awaiting settlement (default: same day at 19:00 or next day at 13:00 GMT+7)',
  'wallet.balActiveHint': 'Ready to disburse',
  'wallet.balReservedHint': 'Disbursement in-flight',
  'wallet.ledgerTitle': 'Balance Mutation',
  'wallet.ledgerSubtitle': 'Records settlement, withdrawal, and balance adjustments (deposits are not recorded).',
  'wallet.mutSettlement': 'Settlement',
  'wallet.mutWithdrawal': 'Withdrawal',
  'wallet.mutAdjustment': 'Balance Adjustment',
  'wallet.mutFee': 'Fee',
  'wallet.mutOther': 'Other',
  'wallet.filters': 'Filters',
  'wallet.activeFilters': '{n} active',
  'wallet.filterMerchant': 'Merchant',
  'wallet.filterAnyMerchant': 'All merchants',
  'wallet.filterType': 'Type',
  'wallet.filterAnyType': 'All types',
  'wallet.filterTrxId': 'TRX ID',
  'wallet.filterTrxIdPlaceholder': 'Search trxId…',
  'wallet.filterCreatedBy': 'Created By',
  'wallet.filterAnyCreatedBy': 'All',
  'wallet.filterApply': 'Apply',
  'wallet.filterClear': 'Clear',
  'wallet.ledgerColType': 'Type',
  'wallet.ledgerColTrxId': 'trxId',
  'wallet.ledgerColPending': 'Δ Pending',
  'wallet.ledgerColActive': 'Δ Active',
  'wallet.ledgerColReserved': 'Δ Reserved',
  'wallet.ledgerColCreatedBy': 'Created by',
  'wallet.ledgerColCreated': 'Created',
  'wallet.ledgerEmpty': 'No ledger activity yet.',
  'wallet.pageRows': 'Page {page} · {count} rows',

  // ===== theme + locale switcher =====
  'theme.toggle': 'Toggle theme',
  'theme.autoUsing': 'Auto (using {scheme}) — click to switch to light',
  'theme.darkClickToAuto': 'Dark — click to switch to auto',
  'theme.lightClickToDark': 'Light — click to switch to dark',
  'locale.label': 'Language',
  'locale.indonesian': 'Indonesian',
  'locale.english': 'English',

  // ===== users =====
  'users.title': 'Users',
  'users.subtitleMerchant': 'Manage users under your merchant.',
  'users.subtitleInternal': 'Manage TransPay internal users & merchant users.',
  'users.activeFilters': '{n} active',
  'users.filterSearch': 'Search',
  'users.filterSearchPh': 'Email or name',
  'users.filterMerchant': 'Merchant',
  'users.filterRole': 'Role',
  'users.filterRolePh': 'All roles',
  'users.newUser': 'New User',
  'users.tabInternal': 'Internal Users',
  'users.tabExternal': 'Merchant Users',
  'users.colEmail': 'Email',
  'users.colName': 'Name',
  'users.colRole': 'Role',
  'users.colMerchant': 'Merchant',
  'users.colLastLogin': 'Last login',
  'users.colCreated': 'Created',
  'users.empty': 'No users yet.',
  'users.badgeSuperuser': 'superuser',
  'users.noRole': '— no role —',
  'users.menuIpWhitelist': 'IP whitelist',
  'users.menuEditUser': 'Edit user',
  'users.menuResetMfa': 'Reset MFA',
  'users.menuMfaInactive': 'MFA inactive',
  'users.resetMfaTitle': 'Reset MFA — {email}',
  'users.resetMfaWarning':
    "This disables the MFA for user {email}. The user must re-enroll via the Security page before doing disbursement again. The action is recorded in the audit log.",
  'users.resetMfaConfirm': 'Reset MFA now',
  'users.suspendedBadge': 'Suspended',
  'users.menuUnsuspend': 'Unsuspend',
  'users.unsuspendTitle': 'Unsuspend — {email}',
  'users.unsuspendWarning':
    "This re-enables user {email} and resets the failed-login counter to 0. The action is recorded in the audit log.",
  'users.unsuspendConfirm': 'Unsuspend',
  'users.suspendReason.too_many_failed_logins': 'Too many failed logins',
  'users.suspendReason.merchant_suspended_cascade': 'Suspended with merchant',
  'users.suspendReason.manual': 'Manual by admin',
  'users.suspendDetail': 'Since {date} · {reason}',
  'users.ipWhitelistTitle': 'IP Whitelist · {email}',
  'users.ipWhitelistDenyAll':
    'Deny-all default. Without at least 1 entry this user will hit {token} on the next login. When rotating IP, add the new IP first before removing the old one.',
  'users.ipWhitelistAddCidr': 'Add IP',
  'users.ipWhitelistCidrLabel': 'IP Address',
  'users.ipWhitelistCidrPlaceholder': '203.0.113.42',
  'users.ipWhitelistRequired': 'Required',
  'users.ipWhitelistInvalidIp': 'Invalid IPv4 address (e.g. 203.0.113.42)',
  'users.ipWhitelistLabelLabel': 'Label',
  'users.ipWhitelistLabelPlaceholder': 'HQ Jakarta / VPN',
  'users.ipWhitelistExisting': 'Existing entries',
  'users.ipWhitelistColCidr': 'IP',
  'users.ipWhitelistColLabel': 'Label',
  'users.ipWhitelistColEnabled': 'Enabled',
  'users.ipWhitelistColCreatedBy': 'Created by',
  'users.ipWhitelistColLastUsed': 'Last used',
  'users.ipWhitelistEmpty':
    'No IP whitelist yet. This user will not be able to log in until there is at least one entry.',
  'users.ipWhitelistEnabledYes': 'yes',
  'users.ipWhitelistEnabledNo': 'no',
  'users.ipWhitelistRemove': 'Remove',
  'users.ipWhitelistClose': 'Close',
  'users.createTitle': 'Create {scope} user',
  'users.emailLabel': 'Email',
  'users.accountNameLabel': 'Account name',
  'users.initialPasswordLabel': 'Initial password',
  'users.initialPasswordDesc': 'min 12 chars; user can change after first login',
  'users.merchantLabel': 'Merchant',
  'users.merchantDesc':
    'The merchant this user works for. The user will only see data from this merchant.',
  'users.merchantPlaceholderLoading': 'loading…',
  'users.merchantPlaceholderSelect': 'Select merchant',
  'users.merchantNothingFound': 'No merchants',
  'users.roleLabel': 'Role',
  'users.roleDesc': 'Permissions are granted by role assignment.',
  'users.newPasswordLabel': 'New password',
  'users.newPasswordDesc': 'leave blank to keep existing (min 12 chars)',
  'users.editTitle': 'Edit {email}',
  'users.deleteTitle': 'Delete user',
  'users.deleteConfirm': 'Delete user {email}?',
  'users.deleteWarning': 'This action is permanent. The user can no longer log in after deletion.',

  // ===== roles =====
  'roles.title': 'Roles & Permissions',
  'roles.subtitle': 'Configure permissions per role. System roles can still have their grants edited.',
  'roles.newRole': 'New Role',
  'roles.internalOnlyAlert':
    'This page is only accessible by TransPay internal staff. Contact your admin if you need a role change.',
  'roles.noManagePermission':
    'You do not have permission to manage roles. Ask an admin to grant {permA} or {permB}.',
  'roles.tabInternal': 'Internal Roles',
  'roles.tabMerchant': 'Merchant Roles',
  'roles.empty': 'No roles.',
  'roles.selectPrompt': 'Select a role on the left.',
  'roles.badgeSystem': 'system',
  'roles.savedSummary':
    'Saved — {added} added, {removed} removed, {affected} cached user session(s) invalidated.',
  'roles.uncatalogedKeys':
    'New permission keys found that are not yet in the feature catalog — they cannot be toggled from this UI: {keys}',
  'roles.colCreate': 'Create',
  'roles.colView': 'View',
  'roles.colEdit': 'Edit',
  'roles.colSuspend': 'Suspend / Delete',
  'roles.granted': '{granted} / {total} granted',
  'roles.clearAll': 'Clear all',
  'roles.grantAll': 'Grant all',
  'roles.colFeature': 'FEATURE',
  'roles.colExtras': 'EXTRAS',
  'roles.appliesInternal': 'Internal',
  'roles.appliesMerchant': 'Merchant',
  'roles.appliesBoth': 'Both',
  'roles.tooltipInternal': 'TransPay internal staff only',
  'roles.tooltipMerchant': 'Merchant + sub-users under the merchant',
  'roles.tooltipBoth': 'Internal & merchant — both',
  'roles.createTitle': 'Create Role',
  'roles.nameLabel': 'Name',
  'roles.slugLabel': 'Slug',
  'roles.slugDesc': 'lower_snake_case wire identifier, e.g. merchant_ops_lead',
  'roles.scopeLabel': 'Scope',
  'roles.descriptionLabel': 'Description',
  // ----- feature catalog -----
  'roles.catBusiness': 'Business Activity',
  'roles.catBusinessHint': 'The main pages users see every day.',
  'roles.catMerchantOps': 'Merchant Management',
  'roles.catMerchantOpsHint':
    'Merchant onboarding & operations — mostly for TransPay internal staff.',
  'roles.catUsersRoles': 'Users & Roles',
  'roles.catUsersRolesHint': 'Access to the user list, invitations, and role settings.',
  'roles.catWallet': 'Wallet & Finance',
  'roles.catWalletHint': 'Balances, ledger, and wallet adjustments.',
  'roles.catSecurity': 'Security & API Access',
  'roles.catSecurityHint': 'API credentials & IP whitelist.',
  'roles.catAudit': 'Audit & System',
  'roles.catAuditHint': 'Activity history, API traces, and platform settings.',
  'roles.featOverview': 'Dashboard / Overview',
  'roles.featDeposits': 'Deposit (Cash-in)',
  'roles.featDisbursements': 'Disbursement (Cash-out)',
  'roles.featDisbursementsDesc': 'Only merchants can create disbursements.',
  'roles.featTransactionReport': 'Transaction Report',
  'roles.featTransactionReportDesc':
    'Daily auto-generated XLSX (cashin + cashout). View shows the list, Download retrieves the file.',
  'roles.extraDownloadTransactionReport': 'Download report file',
  'roles.extraViewAllMerchantReports': 'View all merchants’ reports (internal-only)',
  'roles.featMerchants': 'Merchant',
  'roles.featWhDeliveryOwn': 'Webhook Delivery (Own)',
  'roles.featWhDeliveryAll': 'Webhook Delivery (All Merchants)',
  'roles.featSubUsers': 'Merchant Sub-User',
  'roles.featSubUsersDesc': 'Staff accounts within a single merchant.',
  'roles.featInternalUsers': 'Internal User (TransPay)',
  'roles.featInternalUsersDesc': 'TransPay staff (admin, ops, finance, CS).',
  'roles.featResetMfa': 'Reset User MFA',
  'roles.featResetMfaDesc': 'Disable another user\'s MFA so they can re-enroll (e.g. lost device). Internal: any user; merchant admin: users in own merchant.',
  'roles.featRoles': 'Roles & Permissions',
  'roles.featRolesDesc': 'Who may manage which roles. Internal staff only.',
  'roles.featWalletOwn': 'Wallet (Own)',
  'roles.featWalletAny': 'Wallet (Any Merchant)',
  'roles.featApiCredentials': 'API Credentials (Own)',
  'roles.extraRevealToken': 'Reveal Token (Plaintext)',
  'roles.featIpwUser': 'IP Whitelist — User Login',
  'roles.featIpwMerchant': 'IP Whitelist — Merchant API',
  'roles.featIpwInternal': 'IP Whitelist — Internal Users',
  'roles.featAuditLog': 'Audit Log',
  'roles.featApiActivity': 'API Activity (per Transaction)',
  'roles.featApiActivityDesc': 'Only internal staff can see traces to the upstream provider.',
  'roles.featSettings': 'Platform Settings',
  'roles.featApiDocs': 'API Documentation',
  'roles.featApiDocsDesc': 'Access the API Documentation page. The endpoint list inside is still filtered by the user\'s permissions.',
  'roles.extraViewBalances': 'View Balances',
  'roles.extraViewTrxCount': 'View Trx Count',
  'roles.extraViewCharts': 'View Charts',
  'roles.extraViewDetail': 'View Detail',
  'roles.extraManageCredentials': 'Manage Credentials',
  'roles.extraManageWebhookUrl': 'Manage Webhook URL',
  'roles.extraViewAllMerchants': 'View All Merchants',
  'roles.extraUnsuspendMerchant': 'Unsuspend Merchant',
  'roles.extraUnsuspendUser': 'Unsuspend User',
  'roles.extraResetUserMfa': 'Reset User MFA',
  'roles.extraRetry': 'Retry',
  'roles.extraMarkDead': 'Mark Dead',
  'roles.extraUpdateStatus': 'Update status (manual)',
  'roles.extraManageMerchantRole': 'Manage Merchant Role',
  'roles.extraManageInternalRole': 'Manage Internal Role',
  'roles.extraDelegate': 'Delegate',
  'roles.extraViewLedger': 'View Ledger',
  'roles.extraAdjustAudited': 'Adjust (Audited)',
  'roles.extraViewPayokActivity': 'View PAYOK Activity',
  'roles.extraViewPaymentMethods': 'View Merchant Payment Methods',
  'roles.extraManagePaymentMethods': 'Manage Merchant Payment Methods',
  'roles.extraManageApiDisbursement': 'Manage API Disbursement Gate (money-out)',
  'roles.extraManageAcquirers': 'Manage Acquirers',

  // ===== settings =====
  'settings.title': 'Platform Settings',
  'settings.subtitle':
    'Operator-tunable business config. Every change is stored in the DB and recorded in the audit log. Reset to default to revert to the hardcoded value in code.',
  'settings.reload': 'Reload',
  'settings.superuserOnly': 'Only the superadmin can access Platform Settings.',
  'settings.failedToLoad': 'Failed to load settings',
  'settings.countSingular': '{n} setting',
  'settings.countPlural': '{n} settings',
  'settings.overrideBadge': 'operator override',
  'settings.defaultBadge': 'code default',
  'settings.savedAlert':
    'Saved. Cache invalidated; next read picks up the new value within 1 minute.',
  'settings.defaultLabel': 'Default:',
  'settings.lastChanged': '· last changed by {user} at {at}',
  'settings.resetToDefault': 'Reset to default',
  'settings.catSettlement': 'Settlement',
  'settings.catPaymentMethods': 'Payment Methods',
  'settings.catFees': 'Fees',
  'settings.catAuth': 'Auth',
  'settings.catWebhook': 'Webhook',
  'settings.settlementWindowDaysLabel': 'Day offset',
  'settings.settlementWindowDaysDesc': '0 = same day, 1 = next day, and so on.',
  'settings.settlementWindowHourLabel': 'Settlement hour (GMT+7)',
  'settings.settlementWindowHourDesc': '{min}–23',
  'settings.settlementWindowPreviewSameDay': 'Settles the same day at {hour}:00 GMT+7.',
  'settings.settlementWindowPreviewNextDay': 'Settles the next day at {hour}:00 GMT+7.',
  'settings.settlementWindowPreviewDays': 'Settles {n} days after it was paid, at {hour}:00 GMT+7.',
  'settings.enabledMethodsLabel': 'Enabled methods',
  'settings.basisPointsLabel': 'Basis points',
  'settings.basisPointsDesc': '100 bps = 1%',
  'settings.idrLabel': 'IDR',
  'settings.catSettlementException': 'Custom Settlement Exception',
  'settings.exc.methodsLabel': 'Payment method',
  'settings.exc.methodsDesc':
    'Check the payment methods that use the exception rule. Unchecked methods follow the two regular settlement windows above.',
  'settings.exc.datesLabel': 'Settlement hour & exception dates per payment method',
  'settings.exc.datesDesc':
    'A checked method settles H+1 at its own settlement hour. Exception dates are dates settlement SKIPS. Saturdays & Sundays are selected automatically — click a date in the calendar to select or unselect it. Past dates cannot be selected.',
  'settings.exc.pickDates': 'Pick exception dates',
  'settings.exc.noneChecked': 'No payment method checked — every settlement follows the two regular settlement windows.',
  'settings.exc.hourLabel': 'Settlement hour (0-23, GMT+7)',
  'settings.exc.summaryHour': 'H+1 at {hour}:00',
  'settings.exc.weekendDefault': 'skips every Saturday & Sunday',
  'settings.exc.extraCount': '+{n} extra date(s)',
  'settings.exc.weekendOffCount': '{n} weekend date(s) unselected',
  'settings.exc.extraDates': 'Extra dates:',
  'settings.exc.weekendOffDates': 'Unselected weekends (settlement may land here):',
  'settings.exc.calendarHint': 'Selected = skipped by settlement (GMT+7)',
  'settings.noEditorTitle':
    'No dedicated editor for this setting yet. Showing JSON for inspection only — edit from the API directly if needed.',

  // ===== merchants =====
  'merchants.title': 'Merchants',
  'merchants.subtitle': 'Manage onboarded merchants',
  'merchants.newMerchant': 'New Merchant',
  'merchants.colId': 'id',
  'merchants.colName': 'Name',
  'merchants.colEmail': 'Email',
  'merchants.colDepositFee': 'Deposit fee (default)',
  'merchants.colDisbFee': 'Disb fee (default)',
  'merchants.feeOverrideVa': '{n} VA',
  'merchants.feeOverrideType': '{n} type(s)',
  'merchants.feeOverrideVaTitle': 'VA fee',
  'merchants.feeOverrideVaSub': 'other methods use default',
  'merchants.feeOverrideVaDefault': 'Default (non-VA)',
  'merchants.feeOverrideDisbTitle': 'Disbursement fee',
  'merchants.feeOverrideDisbSub': 'other types use default',
  'merchants.feeOverrideDisbDefault': 'Default',
  'merchants.colActiveBalance': 'Active Balance',
  'merchants.colPendingBalance': 'Pending Balance',
  'merchants.colCashIn': 'Total Cash In',
  'merchants.colCashOut': 'Total Cash Out',
  'merchants.colApi': 'API',
  'merchants.colStatus': 'Status',
  'merchants.colCreated': 'Created',
  'merchants.empty': 'No merchants yet. Click New Merchant to create one.',
  'merchants.activeFilters': '{n} active',
  'merchants.filterSearch': 'Search',
  'merchants.filterSearchPh': 'Merchant name or email',
  'merchants.filterStatus': 'Status',
  'merchants.filterApi': 'API access',
  'merchants.filterAll': 'All',
  'merchants.apiEnabled': 'enabled',
  'merchants.apiDisabled': 'disabled',
  'merchants.statusSuspended': 'suspended',
  'merchants.statusActive': 'active',

  // ===== api credentials =====
  'apiCred.title': 'API Credentials',
  'apiCred.merchantOnly': 'The API Credentials page is only for merchant users.',
  'apiCred.subtitle':
    'Credentials for server-to-server integration to `/api/v1/cashin` and `/api/v1/cashout`',
  'apiCred.activeCredentials': 'Active credentials',
  'apiCred.viewToken': 'View token',
  'apiCred.regenerateToken': 'Regenerate token',
  'apiCred.clientIdLabel': 'Client ID',
  'apiCred.clientIdDesc': 'Public identifier — safe to store in repo / config.',
  'apiCred.clientTokenLabel': 'Client Token',
  'apiCred.webhookSecretLabel': 'Webhook Secret',
  'apiCred.webhookSecretDesc': 'HMAC key to verify inbound webhooks and sign API disbursement requests. Secret — store securely.',
  'apiCred.tokenHiddenAdmin':
    'Token hidden (argon2id hash verification on the auth path). Click "View token" — enter your password to reveal the plaintext (audited).',
  'apiCred.tokenHiddenNonAdmin':
    'Token hidden (argon2id hash verification on the auth path). The plaintext can only be revealed by a user with the "Reveal Own API Token" permission. Contact your admin to request access.',
  'apiCred.apiAccess': 'API Access',
  'apiCred.usageHttp': 'How to use (HTTP)',
  'apiCred.merchantUpdated': 'Merchant updated: {at}',
  'apiCred.errInvalidPassword': 'Wrong password. Try again.',
  'apiCred.errTokenNotRevealable':
    'Token is not yet encrypted in the DB (legacy merchant, before the reveal feature). Click Regenerate token once to enable it.',
  'apiCred.errKmsDecrypt': 'KMS failed to decrypt. Check the KMS configuration / contact the platform admin.',
  'apiCred.errPasswordRequired': 'Password is required.',
  'apiCred.verifyPasswordTitle': 'Verify password',
  'apiCred.verifyPasswordIntro':
    'To reveal the plaintext token, re-enter your account password. This action is logged in the audit with your email + IP.',
  'apiCred.accountPasswordLabel': 'Account password',
  'apiCred.mfaCodeLabel': 'MFA code',
  'apiCred.mfaCodeHint': 'Required if your account has MFA enabled (6-digit authenticator code, or a backup code).',
  'apiCred.mfaCodePlaceholder': '123456',
  'apiCred.errMfaRequired': 'Your account has MFA enabled — enter your MFA code to reveal the token.',
  'apiCred.errInvalidMfaCode': 'MFA code is wrong or expired. Try again.',
  'apiCred.errMfaUnavailable': 'MFA verification is unavailable right now. Try again or contact your admin.',
  'apiCred.tokenPlaintextTitle': 'Plaintext token',
  'apiCred.tokenRevealedWarning':
    'This is your active token. This action is logged in the audit. Make sure to store it somewhere safe and close the modal when done.',
  'apiCred.close': 'Close',
  'apiCred.copied': 'Copied',
  'apiCred.copy': 'Copy',

  // ===== merchant detail =====
  'merchantDetail.back': 'Back',
  'merchantDetail.copied': 'Copied',
  'merchantDetail.copy': 'Copy',
  'merchantDetail.cancel': 'Cancel',
  'merchantDetail.close': 'Close',
  'merchantDetail.reset': 'Reset',
  'merchantDetail.badgeSuspended': 'Suspended',
  'merchantDetail.badgeActive': 'Active',
  'merchantDetail.suspend': 'Suspend',
  'merchantDetail.unsuspend': 'Unsuspend',
  'merchantDetail.revealToken': 'Reveal token',
  'merchantDetail.regenerateCredentials': 'Regenerate credentials',
  'merchantDetail.tabInfo': 'Info',
  'merchantDetail.tabCredentials': 'Credentials',
  'merchantDetail.tabIpWhitelist': 'API IP Whitelist',
  'merchantDetail.tabPayok': 'PAYOK Settings',
  'merchantDetail.tabWallet': 'Wallet',
  'merchantDetail.tabPaymentMethods': 'Payment Methods',
  'merchantDetail.pmTitle': 'Cash-in payment methods',
  'merchantDetail.pmDesc': 'Toggle which cash-in payment methods this merchant may use. Limited to the methods enabled platform-wide.',
  'merchantDetail.pmReadOnly': 'Read-only — you can view but not edit these payment methods.',
  'merchantDetail.pmInheriting': 'Inheriting the platform default — all available methods are enabled for this merchant.',
  'merchantDetail.pmRestricted': 'Restricted — {n} method(s) enabled for this merchant.',
  'merchantDetail.pmSelectAll': 'Select all',
  'merchantDetail.pmSave': 'Save payment methods',
  'merchantDetail.pmNoneWarning': 'At least one method must stay enabled.',
  'merchantDetail.apiDisbTitle': 'Server-to-server disbursement (API)',
  'merchantDetail.apiDisbDesc': 'Allow this merchant to initiate withdrawals (money-out) via the API key, not just the dashboard.',
  'merchantDetail.apiDisbWarning': 'High-risk: a leaked API key from a whitelisted IP could move money out. Enable only for trusted, integrated merchants. Requests must be HMAC-signed.',
  'merchantDetail.vaFeesTab': 'VA Fees',
  'merchantDetail.vaFeesDesc': 'Cash-in fee per Virtual Account method: a percentage (%) PLUS a flat IDR amount, both applied — fee = round(amount × %) + flat. Leave both fields blank to fall back to the merchant percentage-only deposit fee for that VA. QRIS, e-wallet and OTC always use the percentage fee.',
  'merchantDetail.vaFeesFallback': 'blank = % deposit fee',
  'merchantDetail.vaFeesPercentLabel': 'Percent (%)',
  'merchantDetail.vaFeesFixedLabel': 'Flat (IDR)',
  'merchantDetail.vaFeesSave': 'Save VA fees',
  'merchantDetail.disbursementFeesTab': 'Disbursement Fees',
  'merchantDetail.disbursementFeesDesc': 'Cash-out fee per beneficiary account type (Bank, E-wallet, BPR): a percentage (%) PLUS a flat IDR amount, both applied on the amount — fee = round(amount × %) + flat. The fee is added on top; the beneficiary still receives the full amount. Leave both fields blank to fall back to the merchant flat default disbursement fee for that type.',
  'merchantDetail.disbursementFeesFallback': 'blank = default fee',
  'merchantDetail.disbursementFeesSave': 'Save disbursement fees',
  // credentials tab
  'merchantDetail.transpayClientId': 'Transpay Client ID',
  'merchantDetail.tokenSuffix': 'Token suffix',
  'merchantDetail.tokenSuffixHint': 'Auth path verifies via argon2id hash.',
  'merchantDetail.tokenSuffixHintAdmin':
    'Click Reveal token in the header to view the plaintext (KMS decrypt, audited).',
  'merchantDetail.tokenSuffixHintNonAdmin': 'Plaintext is not shown here.',
  // password prompt modal
  'merchantDetail.verifyPasswordTitle': 'Verify password',
  'merchantDetail.verifyPasswordIntro':
    'To reveal the plaintext token, re-enter your account password. This action is logged in the audit with your email + IP.',
  'merchantDetail.accountPasswordLabel': 'Account password',
  'merchantDetail.errPasswordWrong': 'Wrong password. Try again.',
  'merchantDetail.errTokenNotRevealable':
    'Token is not encrypted yet (legacy merchant, before the reveal feature). Click Regenerate credentials once to enable it.',
  'merchantDetail.errKmsDecrypt': 'KMS failed to decrypt. Check the KMS configuration.',
  'merchantDetail.errPasswordRequired': 'Password is required.',
  // token reveal modal
  'merchantDetail.tokenRotatedTitle': 'New credentials issued',
  'merchantDetail.tokenPlaintextTitle': 'Token plaintext',
  'merchantDetail.tokenRotatedWarning':
    'The old token is no longer valid. The plaintext token is only shown here — copy & save it now.',
  'merchantDetail.tokenRevealedWarning':
    "The merchant's active token. The reveal action is logged in audit_log with email + IP. Close the modal when done.",
  'merchantDetail.transpayClientToken': 'Transpay Client Token',
  'merchantDetail.webhookSecret': 'Webhook Secret',
  // IP whitelist panel
  'merchantDetail.ipwlDenyAll':
    'Default deny-all. Without at least 1 whitelisted IP, `POST /api/v1/cashin` will return 403 `ip_whitelist_required`.',
  'merchantDetail.ipwlAddCidr': 'Add IP',
  'merchantDetail.ipwlCidrLabel': 'IP Address',
  'merchantDetail.ipwlCidrPlaceholder': '203.0.113.42',
  'merchantDetail.ipwlLabelLabel': 'Label',
  'merchantDetail.ipwlLabelPlaceholder': 'dev / merchant-prod / etc',
  'merchantDetail.ipwlRequired': 'Required',
  'merchantDetail.ipwlInvalidIp': 'Invalid IPv4 address (e.g. 203.0.113.42)',
  'merchantDetail.ipwlColCidr': 'IP',
  'merchantDetail.ipwlColLabel': 'Label',
  'merchantDetail.ipwlColCreatedBy': 'Created by',
  'merchantDetail.ipwlColCreated': 'Created',
  'merchantDetail.ipwlColLastUsed': 'Last used',
  'merchantDetail.ipwlRemove': 'Remove',
  'merchantDetail.ipwlEmpty': 'No entries yet. Add at least 1 to enable the API.',
  // merchant info edit panel
  'merchantDetail.identityReadOnly': 'Identity (read-only)',
  'merchantDetail.fieldMerchantId': 'Merchant ID',
  'merchantDetail.fieldCreated': 'Created',
  'merchantDetail.fieldLastUpdated': 'Last updated',
  'merchantDetail.fieldStatus': 'Status',
  'merchantDetail.statusSuspendedAt': 'Suspended at {at}',
  'merchantDetail.statusActive': 'Active',
  'merchantDetail.basics': 'Basics',
  'merchantDetail.nameLabel': 'Name',
  'merchantDetail.emailLabel': 'Email',
  'merchantDetail.apiAccessEnabled': 'API access enabled',
  'merchantDetail.apiAccessEnabledDesc': 'When disabled, /api/v1/cashin will reject with 403',
  'merchantDetail.fees': 'Fees',
  'merchantDetail.depositFeeBps': 'Deposit fee (bps)',
  'merchantDetail.depositFeeBpsDesc': '{pct} % per cash-in transaction',
  'merchantDetail.disbursementFeeIdr': 'Disbursement fee (IDR)',
  'merchantDetail.disbursementFeeIdrDesc': 'Flat IDR per cash-out transaction',
  'merchantDetail.transactionLimits': 'Transaction Limits (IDR per transaction)',
  'merchantDetail.transactionLimitsDesc':
    'Per-transaction cash-in and cash-out amount limits for this merchant. There is no daily limit — only per-transaction. When a user sends an amount outside the range, the request is rejected immediately before reaching PAYOK (deposit) or before reserving the wallet (disbursement).',
  'merchantDetail.cashinMin': 'Cash-in min',
  'merchantDetail.cashinMinDesc': 'Minimum amount per deposit transaction',
  'merchantDetail.cashinMax': 'Cash-in max',
  'merchantDetail.cashinMaxDesc': 'Maximum amount per deposit transaction',
  'merchantDetail.cashoutMin': 'Cash-out min',
  'merchantDetail.cashoutMinDesc': 'Minimum amount per disbursement transaction',
  'merchantDetail.cashoutMax': 'Cash-out max',
  'merchantDetail.cashoutMaxDesc':
    'Maximum amount per disbursement transaction (PAYOK IDN cap: Rp 50,000,000)',
  'merchantDetail.webhookUrls': 'Webhook URLs (TransPay → Merchant)',
  'merchantDetail.webhookUrlsDesc':
    "TransPay will POST a signed event to this URL after a transaction settles. The X-Transpay-Signature header is computed as HMAC-SHA256 of the merchant's webhookSecret.",
  'merchantDetail.depositWebhookUrl': 'Deposit webhook URL',
  'merchantDetail.disbursementWebhookUrl': 'Disbursement webhook URL',
  'merchantDetail.changesSaved': 'Changes saved.',
  'merchantDetail.saveChanges': 'Save changes',
  // validation
  'merchantDetail.valRequired': 'Required',
  'merchantDetail.valInvalidEmail': 'Invalid email',
  'merchantDetail.valInvalidUrl': 'Invalid URL (must start with http:// or https://)',
  'merchantDetail.valDepositFeeRange': '0–10000 (1 bps = 0.01 %)',
  'merchantDetail.valDisbFeeRange': '0–999,999,999',
  'merchantDetail.valMinRp1': 'min Rp 1',
  'merchantDetail.valMaxGteMin': 'max must be ≥ min',
  // PAYOK settings panel
  'merchantDetail.payokEnabledTitle': 'PAYOK V3.6 integration {state}',
  'merchantDetail.payokStateEnabled': 'enabled',
  'merchantDetail.payokStateDisabled': 'disabled',
  'merchantDetail.payokConnection': 'Connection',
  'merchantDetail.payokEnable': 'Enable PAYOK',
  'merchantDetail.payokEnableDesc': 'Turn payments on/off for this merchant via the default acquirer.',
  'merchantDetail.payokManagedNote': 'Connection config (base URL, MID, endpoints, keys, notification URLs) is now managed centrally in the Acquirer menu. Only the on/off toggle is set per merchant here.',
  'merchantDetail.payokSaveSettings': 'Save PAYOK settings',
  'merchantDetail.payokUnavailable':
    'The PAYOK status of this merchant is only visible to users with the merchant edit permission.',
  // rotate keypair confirm modal
  // rotate keypair result modal
  // wallet panel
  'merchantDetail.balances': 'Balances',
  'merchantDetail.adjustActiveBalance': 'Adjust active balance',
  'merchantDetail.balPending': 'Pending Balance',
  'merchantDetail.balActive': 'Active Balance',
  'merchantDetail.balReserved': 'Reserved Balance',
  'merchantDetail.balOutstanding': 'Outstanding (carry-over)',
  'merchantDetail.balOutstandingValue': '{amount} — auto-deducted from next settlements',
  'merchantDetail.balLifetimeDeposit': 'Lifetime Deposit',
  'merchantDetail.balLifetimeDisbursement': 'Lifetime Disbursement',
  'merchantDetail.balLifetimeFee': 'Lifetime Fee',
  'merchantDetail.balUpdated': 'Updated',
  // adjust balance modal
  'merchantDetail.adjustCurrentActive': 'Current active balance: ',
  'merchantDetail.adjustOutstanding': ' · Outstanding: ',
  'merchantDetail.adjustAdd': 'Add',
  'merchantDetail.adjustDeduct': 'Deduct',
  'merchantDetail.adjustAmount': 'Amount (IDR)',
  'merchantDetail.adjustExceedsPre': 'Deduct exceeds active balance by ',
  'merchantDetail.adjustExceedsMid': '{deducted} will be deducted now; the remaining ',
  'merchantDetail.adjustExceedsPost':
    ' can be carried over to upcoming settlements until paid off.',
  'merchantDetail.adjustCarryLabel': 'Carry over {amount} to next settlement(s)',
  'merchantDetail.adjustReason': 'Reason',
  'merchantDetail.adjustReasonDesc':
    'Recorded in the wallet ledger and audit log (min 10 chars).',
  'merchantDetail.adjustReasonPlaceholder':
    'e.g. Manual correction for chargeback ref #1234',
  'merchantDetail.adjustReasonError': 'At least 10 characters',
  'merchantDetail.adjustAddToActive': 'Add to active',
  'merchantDetail.adjustDeductFromActive': 'Deduct from active',

  // ===== notifications (toast wording, per action) =====
  'notify.user.create.ok': 'User created',
  'notify.user.create.fail': 'Failed to create user',
  'notify.user.update.ok': 'User updated',
  'notify.user.update.fail': 'Failed to update user',
  'notify.user.delete.ok': 'User deleted',
  'notify.user.delete.fail': 'Failed to delete user',
  'notify.user.selfEditTitle': 'Not allowed',
  'notify.user.selfEditMsg': 'You cannot edit your own account.',
  'notify.mfa.reset.ok': "User's MFA reset",
  'notify.mfa.reset.fail': 'Failed to reset MFA',
  'notify.user.unsuspend.ok': 'User unsuspended',
  'notify.user.unsuspend.fail': 'Failed to unsuspend user',
  'notify.mfa.enable.ok': 'MFA enabled',
  'notify.mfa.enable.fail': 'Failed to enable MFA',
  'notify.mfa.disable.ok': 'MFA disabled',
  'notify.mfa.disable.fail': 'Failed to disable MFA',
  'notify.ipwl.add.ok': 'IP whitelist entry added',
  'notify.ipwl.add.fail': 'Failed to add IP whitelist entry',
  'notify.ipwl.remove.ok': 'IP whitelist entry removed',
  'notify.ipwl.remove.fail': 'Failed to remove IP whitelist entry',
  'notify.role.save.ok': 'Role saved',
  'notify.role.save.fail': 'Failed to save role',
  'notify.role.create.ok': 'Role created',
  'notify.role.create.fail': 'Failed to create role',
  'notify.setting.save.ok': 'Setting saved',
  'notify.setting.save.fail': 'Failed to save setting',
  'notify.setting.reset.ok': 'Setting reset to default',
  'notify.setting.reset.fail': 'Failed to reset setting',
  'notify.merchant.create.ok': 'Merchant created',
  'notify.merchant.create.fail': 'Failed to create merchant',
  'notify.merchant.save.ok': 'Merchant saved',
  'notify.merchant.save.fail': 'Failed to save merchant',
  'notify.report.regen.ok': 'Report queued for regeneration',
  'notify.report.regen.fail': 'Failed to queue report regeneration',
  'notify.merchant.suspend.ok': 'Merchant suspended',
  'notify.merchant.suspend.fail': 'Failed to suspend merchant',
  'notify.merchant.unsuspend.ok': 'Merchant reactivated',
  'notify.merchant.unsuspend.fail': 'Failed to reactivate merchant',
  'notify.merchant.regenCreds.ok': 'New credentials issued',
  'notify.merchant.regenCreds.fail': 'Failed to issue credentials',
  'notify.disbursement.create.ok': 'Disbursement created',
  'notify.disbursement.create.fail': 'Failed to create disbursement',
  'notify.webhook.resend.ok': 'Webhook resent',
  'notify.webhook.resend.fail': 'Failed to resend webhook',
  'notify.trx.status.ok': 'Transaction status updated',
  'notify.trx.status.fail': 'Failed to update transaction status',

  // Manual status override control (deposit + disbursement detail)
  'trxStatus.selectPlaceholder': 'Change status to…',
  'trxStatus.resendCallback': 'Resend callback to merchant',
  'trxStatus.updateButton': 'Update status',
  'trxStatus.confirmTitle': 'Manually change the transaction status?',
  'trxStatus.confirmBody':
    'Transaction {trxId} will change from {from} to {to}. The merchant wallet is adjusted to match (exactly as a provider callback would) and the action is written to the audit log.',
  'trxStatus.confirmCallbackOn': 'A "{status}" callback will be sent to the merchant webhook URL.',
  'trxStatus.confirmCallbackNoEvent':
    'No callback will be sent: there is no callback event for the pending status.',
  'trxStatus.confirmCallbackOff': 'The merchant callback will NOT be sent.',
  'trxStatus.confirmYes': 'Yes, change status',
  'trxStatus.noticeChanged': 'Status changed from {from} to {to}.',
  'trxStatus.noticeCallbackQueued':
    'Callback queued to {targetUrl} (outbox {outboxId}); the worker delivers it within seconds.',
  'trxStatus.noticeCallbackSkippedPending':
    'Callback not sent: there is no callback event for the pending status.',
  'trxStatus.noticeCallbackSkippedNoUrl':
    'Callback not sent: the merchant has no webhook URL configured.',
  'trxStatus.noticeCallbackNotRequested': 'Merchant callback not sent (checkbox left unchecked).',
  'notify.invite.accept.ok': 'Account created',
  'notify.invite.accept.fail': 'Failed to accept invite',
  'notify.apidoc.request.ok': 'Request sent',
  'notify.apidoc.request.fail': 'Request failed',
  'notify.balance.adjustTitle': 'Balance adjusted',
  'notify.balance.adjustOk': 'Active balance is now {active}.',
  'notify.balance.adjustCarry':
    'Active balance is now {active}. {carried} carried over to next settlements (outstanding {outstanding}).',

  // ===== depositDetail =====
  'depositDetail.title': 'Deposit detail',
  'depositDetail.resendTooltipReady': 'Re-queue the webhook to the merchant. New delivery ID, same payload.',
  'depositDetail.resendTooltipNotReady': 'No webhook to the merchant yet — deposit is not paid/failed.',
  'depositDetail.resendButton': 'Resend webhook',
  'depositDetail.resendNotice':
    'Webhook re-queued to {targetUrl}. New delivery ID: {deliveryId}. The worker will publish to SQS within ~a few seconds; refresh API Activity to see the result.',
  'depositDetail.fieldReference': 'Reference',
  'depositDetail.fieldMerchant': 'Merchant',
  'depositDetail.fieldMethod': 'Method',
  'depositDetail.fieldFee': 'Fee',
  'depositDetail.fieldMerchantRevenue': 'Merchant Revenue',
  'depositDetail.fieldFromIp': 'From IP',
  'depositDetail.fieldPayer': 'Payer',
  'depositDetail.fieldRrn': 'RRN / Voucher',
  'depositDetail.fieldCreated': 'Created (GMT+7)',
  'depositDetail.fieldPaid': 'Paid (GMT+7)',
  'depositDetail.fieldSettled': 'Settled (GMT+7)',
  'depositDetail.fieldScheduledSettlement': 'Scheduled Settlement (GMT+7)',
  'depositDetail.badgeSettled': 'settled',
  'depositDetail.badgeAwaiting': 'awaiting',
  'depositDetail.fieldRemark': 'Remark',

  // ===== disbursementDetail =====
  'disbursementDetail.title': 'Disbursement detail',
  'disbursementDetail.resendTooltipReady': 'Re-queue the webhook to the merchant. New delivery ID, same payload.',
  'disbursementDetail.resendTooltipNotReady': 'No webhook to the merchant yet — disbursement is not paid/failed.',
  'disbursementDetail.resendButton': 'Resend webhook',
  'disbursementDetail.resendNotice':
    'Webhook re-queued to {targetUrl}. New delivery ID: {deliveryId}.',
  'disbursementDetail.fieldReference': 'Reference',
  'disbursementDetail.fieldMerchant': 'Merchant',
  'disbursementDetail.fieldMethod': 'Method',
  'disbursementDetail.fieldBank': 'Bank',
  'disbursementDetail.fieldAccountNumber': 'Account number',
  'disbursementDetail.fieldHolderName': 'Holder name',
  'disbursementDetail.fieldFee': 'Fee',
  'disbursementDetail.fieldTotalDebit': 'Total debit',
  'disbursementDetail.fieldRrn': 'RRN / Voucher',
  'disbursementDetail.fieldCreated': 'Created (GMT+7)',
  'disbursementDetail.fieldPaid': 'Paid (GMT+7)',
  'disbursementDetail.fieldCreatedBy': 'Created by',
  'disbursementDetail.fieldRemark': 'Remark',
  'disbursementDetail.upstreamProvider': 'Upstream provider',
  'disbursementDetail.fieldMerchantOrderId': 'Merchant Order ID',
  'disbursementDetail.fieldUpstreamOrderId': 'Upstream Order ID',
  'disbursementDetail.fieldInquiryToken': 'Inquiry Token',

  // ===== apiActivity (shared by deposit & disbursement detail) =====
  'apiActivity.title': 'API Activity',
  'apiActivity.descInternalDeposit':
    '<b>Initial Request</b> shows the first request from the merchant and the order create to the upstream provider. <b>Notification Cycles</b> record every incoming upstream callback and all webhook deliveries to the merchant that follow that callback. Each row shows its response time.',
  'apiActivity.descExternalDeposit':
    '<b>Initial Request</b> shows the cashin request from the merchant to TransPay. <b>Webhook Notifications</b> record every notification TransPay sends to the merchant webhook endpoint; one notification can have several attempts if a previous delivery failed. Each row shows its response time.',
  'apiActivity.descInternalDisbursement':
    '<b>Initial Request</b> shows the inquiry + order create from the merchant to TransPay and from TransPay to the upstream provider. <b>Notification Cycles</b> record every upstream callback and the webhook deliveries that follow it. Each row shows its response time.',
  'apiActivity.descExternalDisbursement':
    '<b>Initial Request</b> shows the requests from you to TransPay. <b>Webhook Notifications</b> record the notifications TransPay sends to the merchant webhook endpoint.',
  'apiActivity.reloadTooltip': 'Reload',
  'apiActivity.refresh': 'Refresh',
  'apiActivity.upstreamMerchantOrderId': 'upstream merchantOrderId',
  'apiActivity.upstreamPlatformOrderId': 'upstream platformOrderId',
  'apiActivity.trxId': 'trxId',
  'apiActivity.merchantRowsVisible': 'Merchant rows visible',
  'apiActivity.merchantRowsHidden': 'Merchant rows hidden',
  'apiActivity.upstreamRowsVisible': 'Upstream provider rows visible',
  'apiActivity.reconstructedTooltip':
    'Some rows were reconstructed from legacy storage because this transaction is older than the audit instrumentation. URL & duration fields may be incomplete.',
  'apiActivity.reconstructedBadge': 'Reconstructed from legacy storage',
  'apiActivity.entryCount': '· {n} entries',
  'apiActivity.loadFailed': 'Failed to load timeline',
  'apiActivity.emptyDeposit': 'No audit entries for this transaction yet.',
  'apiActivity.emptyDisbursement': 'No API activity for this trx yet.',
  'apiActivity.notificationCycles': 'Notification Cycles',
  'apiActivity.webhookNotifications': 'Webhook Notifications',
  'apiActivity.orphanEntries': 'Orphan entries',
  'apiActivity.initialRequest': 'Initial Request',
  'apiActivity.initialSubtitleInternalDeposit': 'Cashin call from the merchant + order create to the upstream provider',
  'apiActivity.initialSubtitleExternalDeposit': 'Cashin call from the merchant to TransPay',
  'apiActivity.initialSubtitleInternalDisbursement': '{inbound} call(s) from the merchant + {outbound} call(s) to the upstream provider',
  'apiActivity.initialSubtitleExternalDisbursement': '{inbound} call(s) from you to TransPay',
  'apiActivity.labelMerchantApiRequest': 'Merchant → TransPay (API Request)',
  'apiActivity.labelMerchantApiRequestNoun': 'Merchant API Request',
  'apiActivity.labelUpstreamOrderCreate': 'TransPay → Upstream Provider (Order Create)',
  'apiActivity.labelUpstreamOrderCreateNoun': 'Upstream Order Create',
  'apiActivity.labelMerchantInquiry': 'Merchant → TransPay (Account Inquiry)',
  'apiActivity.labelMerchantOrderCreate': 'Merchant → TransPay (Order Create)',
  'apiActivity.labelMerchantToTranspay': 'Merchant → TransPay',
  'apiActivity.labelPayokInquiry': 'TransPay → PAYOK (Account Inquiry)',
  'apiActivity.labelPayokOrderCreate': 'TransPay → PAYOK (Order Create)',
  'apiActivity.labelPayokOrderQuery': 'TransPay → PAYOK (Order Query)',
  'apiActivity.labelTranspayToPayok': 'TransPay → PAYOK',
  'apiActivity.labelPayokCallback': 'PAYOK → TransPay (Callback received)',
  'apiActivity.labelMerchantWebhook': 'TransPay → Merchant (Webhook attempt)',
  'apiActivity.cycleTitle': 'Cycle #{n}',
  'apiActivity.notificationTitle': 'Notification #{n}',
  'apiActivity.cycleSubtitleOne': '1 upstream callback + {n} merchant webhook attempt',
  'apiActivity.cycleSubtitleMany': '1 upstream callback + {n} merchant webhook attempts',
  'apiActivity.notificationSubtitleOne': '{n} delivery attempt to the merchant webhook endpoint',
  'apiActivity.notificationSubtitleMany': '{n} delivery attempts to the merchant webhook endpoint',
  'apiActivity.summaryErrors': 'errors',
  'apiActivity.summaryWorstHttp': 'worst HTTP {n}',
  'apiActivity.cycleEntries': '{n} entries',
  'apiActivity.upstreamCallbackNoun': 'Upstream Callback',
  'apiActivity.upstreamCallbackLabel': 'Upstream Provider → TransPay (Callback received)',
  'apiActivity.webhookAttemptNoun': 'Webhook Attempt #{n}',
  'apiActivity.webhookAttemptLabel': 'TransPay → Merchant (Webhook attempt #{n})',
  'apiActivity.entryNoun': 'entry',
  'apiActivity.legacyTooltipDeposit': 'Reconstructed from legacy columns',
  'apiActivity.legacyBadge': 'legacy',
  'apiActivity.legacyTooltipDisbursement':
    'Reconstructed from the disbursements row (audit log row missing — trx is pre-instrumentation or created via script).',
  'apiActivity.reconstructedRowBadge': 'reconstructed',
  'apiActivity.durationTooltipInbound': 'Time TransPay took to respond to this inbound call',
  'apiActivity.durationTooltipOutbound': 'Time the remote took to respond to our outbound call',
  'apiActivity.noTiming': 'no timing',
  'apiActivity.statusErr': 'ERR',
  'apiActivity.started': 'Started',
  'apiActivity.completed': 'Completed',
  'apiActivity.responseTime': 'Response Time',
  'apiActivity.request': 'Request',
  'apiActivity.response': 'Response',
  'apiActivity.headers': 'Headers',
  'apiActivity.body': 'Body',

  // ===== disbursements (create wizard + extras) =====
  'disbursements.create': 'Create disbursement',
  'disbursements.mfaBannerTitle': 'MFA not active',
  'disbursements.mfaBannerBody':
    'You <strong>cannot create a disbursement</strong> until MFA is active. Every submission requires a 6-digit code from an authenticator app. Enroll an authenticator (Google Authenticator / Authy / 1Password / Bitwarden) now — one-time setup, about 1 minute.',
  'disbursements.mfaEnrollNow': 'Enroll now',
  'disbursements.wizardTitleForm': 'Create Disbursement',
  'disbursements.wizardTitleConfirm': 'Confirm',
  'disbursements.wizardStep1': 'Step 1 of 2 — validate the destination account with the beneficiary bank. Wallet is not debited yet.',
  'disbursements.wizardBankLabel': 'Destination bank',
  'disbursements.wizardBankLoading': 'loading…',
  'disbursements.wizardBankPlaceholder': 'select bank',
  'disbursements.wizardAccountNumberLabel': 'Account number',
  'disbursements.wizardAccountNumberPlaceholder': '1234567890',
  'disbursements.wizardHolderNameLabel': 'Account holder name',
  'disbursements.wizardHolderNamePlaceholder': 'As per the bank passbook',
  'disbursements.wizardAmountLabel': 'Amount (IDR)',
  'disbursements.wizardAmountPlaceholder': '50000',
  'disbursements.wizardRemarkLabel': 'Remark / description (optional)',
  'disbursements.wizardRemarkPlaceholder': 'Withdrawal #1',
  'disbursements.wizardCancel': 'Cancel',
  'disbursements.wizardValidateAccount': 'Validate account',
  'disbursements.wizardConfirmAlert':
    'Account validated successfully with the beneficiary bank. Check the holder name and total debit below before clicking Submit. Submit debits your active balance by amount + fee.',
  'disbursements.wizardFieldBank': 'Bank',
  'disbursements.wizardFieldAccountNumber': 'Account number',
  'disbursements.wizardFieldVerifiedHolder': 'Holder name (bank-confirmed)',
  'disbursements.wizardFieldAmount': 'Amount',
  'disbursements.wizardFieldFee': 'TransPay fee',
  'disbursements.wizardFieldTotalDebit': 'Total debit from active',
  'disbursements.wizardFieldInquiryToken': 'Inquiry token (audit)',
  'disbursements.wizardPasswordLabel': 'Verify your password',
  'disbursements.wizardPasswordDesc': 'Step-up auth: your password is required to submit a disbursement. The action is recorded in the audit log.',
  'disbursements.wizardMfaLabel': 'MFA code (from authenticator app)',
  'disbursements.wizardMfaPlaceholder': '123456 or XXXX-XXXX',
  'disbursements.wizardMfaDesc': '6-digit code from Google Authenticator / Authy / 1Password / Bitwarden, OR a backup code (XXXX-XXXX). Required for every disbursement.',
  'disbursements.wizardBackEdit': 'Back to edit',
  'disbursements.wizardSubmit': 'Submit disbursement',
  'disbursements.errInvalidPassword': 'Wrong password. Try again.',
  'disbursements.errInvalidMfa': 'MFA code is invalid or has expired. Check your authenticator app.',

  // ===== auditLog =====
  'auditLog.scopeInternal': 'TransPay Staff',
  'auditLog.scopeExternal': 'Merchant',
  'auditLog.scopeSystem': 'System',
  'auditLog.actLoginSuccessLabel': 'Login succeeded',
  'auditLog.actLoginSuccessNarrative': 'Signed in to the dashboard successfully.',
  'auditLog.actLoginFailedLabel': 'Login failed',
  'auditLog.actLoginFailedNarrative': 'Login failed — {reason}',
  'auditLog.reasonWrongPassword': 'Wrong password.',
  'auditLog.reasonUnknownEmail': 'Email is not registered.',
  'auditLog.reasonIpNotWhitelisted': 'IP is not in the whitelist.',
  'auditLog.reasonIpWhitelistNotConfigured': 'Account has no IP whitelist yet.',
  'auditLog.reasonIpUnknown': 'User IP could not be detected.',
  'auditLog.actLogoutLabel': 'Logout',
  'auditLog.actLogoutNarrative': 'Signed out of the dashboard.',
  'auditLog.actCashinCreateLabel': 'Created a deposit transaction',
  'auditLog.actCashinCreateNarrative': 'Deposit {amount} via {method} created.',
  'auditLog.actDisbursementInquiryLabel': 'Checked destination account',
  'auditLog.actDisbursementInquiryNarrative': 'Validated account {accountNumber} ({bank}) for a disbursement of {amount}.',
  'auditLog.actDisbursementCreateLabel': 'Sent money to an account',
  'auditLog.actDisbursementCreateNarrative': 'Disbursement {amount} to {holderName} at {bank} ({accountNumber}).',
  'auditLog.actMerchantCreateLabel': 'Created a new merchant',
  'auditLog.actMerchantCreateNarrative': 'Merchant "{name}" created.',
  'auditLog.actMerchantUpdateLabel': 'Changed merchant data',
  'auditLog.actMerchantUpdateNarrative': 'Merchant settings updated.',
  'auditLog.actMerchantRevealLabel': 'Viewed merchant API token',
  'auditLog.actMerchantRevealNarrative': 'Retrieved plaintext API token (sensitive).',
  'auditLog.actMerchantRegenLabel': 'Regenerated API token',
  'auditLog.actMerchantRegenNarrative': 'API token regenerated. New suffix: {newSuffix}.',
  'auditLog.actMerchantIpAddLabel': 'Added merchant IP whitelist',
  'auditLog.actMerchantIpAddNarrative': 'IP {cidr} added to the merchant API whitelist.',
  'auditLog.actUserIpAddLabel': 'Added user IP whitelist',
  'auditLog.actUserIpAddNarrative': 'IP {cidr} added to the user login whitelist.',
  'auditLog.actUsersCreateLabel': 'Created a new user',
  'auditLog.actUsersCreateNarrative': 'New user: {email}.',
  'auditLog.actRolesUpdateLabel': 'Changed role permissions',
  'auditLog.actRolesUpdateNarrative': 'Role "{roleSlug}" permissions updated.',
  'auditLog.actWebhookDepositResendLabel': 'Resent deposit webhook',
  'auditLog.actWebhookDepositResendNarrative': 'Deposit webhook resent to the merchant.',
  'auditLog.actWebhookDisbursementResendLabel': 'Resent disbursement webhook',
  'auditLog.actWebhookDisbursementResendNarrative': 'Disbursement webhook resent to the merchant.',
  'auditLog.actSettingsUpdateLabel': 'Changed settings',
  'auditLog.actSettingsUpdateNarrative': 'Setting "{key}" updated.',
  'auditLog.actSettingsResetLabel': 'Reset settings',
  'auditLog.actSettingsResetNarrative': 'Setting "{key}" restored to default.',
  'auditLog.noExtraDetail': 'No additional details.',
  'auditLog.fieldAmount': 'Amount',
  'auditLog.fieldFee': 'TransPay Fee',
  'auditLog.fieldTotalDebit': 'Total Debit',
  'auditLog.fieldMerchantRevenue': 'Merchant Revenue',
  'auditLog.fieldBankCode': 'Bank Code',
  'auditLog.fieldBankName': 'Bank Name',
  'auditLog.fieldAccountNumber': 'Account Number',
  'auditLog.fieldHolderName': 'Holder Name',
  'auditLog.fieldVerifiedHolderName': 'Holder Name (PAYOK)',
  'auditLog.fieldMerchantId': 'Merchant ID',
  'auditLog.fieldTrxId': 'Transaction ID',
  'auditLog.fieldReferenceId': 'Reference ID',
  'auditLog.fieldPayokPlatformOrderId': 'PAYOK Platform Order ID',
  'auditLog.fieldPayokMerchantOrderId': 'PAYOK Merchant Order ID',
  'auditLog.fieldUserId': 'User ID',
  'auditLog.fieldUserType': 'User Type',
  'auditLog.fieldIsSuperuser': 'Superuser',
  'auditLog.fieldReason': 'Reason',
  'auditLog.fieldMethod': 'Method',
  'auditLog.fieldIp': 'IP',
  'auditLog.fieldCidr': 'CIDR',
  'auditLog.fieldNewSuffix': 'New Token Suffix',
  'auditLog.fieldKey': 'Setting Key',
  'auditLog.fieldEmail': 'Email',
  'auditLog.fieldName': 'Name',
  'auditLog.fieldJti': 'JWT ID',
  'auditLog.fieldNewOutboxId': 'New Outbox ID',
  'auditLog.fieldNewDeliveryId': 'New Delivery ID',
  'auditLog.fieldSourceOutboxId': 'Source Outbox ID',
  'auditLog.fieldSourceStatus': 'Source Status',
  'auditLog.fieldTargetUrl': 'Target URL',
  'auditLog.fieldRemark': 'Remark',
  'auditLog.boolYes': 'Yes',
  'auditLog.boolNo': 'No',
  'auditLog.accessDenied': 'The audit log is only accessible to internal users with the audit.view permission.',
  'auditLog.title': 'Audit Log',
  'auditLog.subtitle':
    'Forensics for all mutating actions: login, credentials, disbursement, cashin, webhook resend, settings. Internal-only.',
  'auditLog.refresh': 'Refresh',
  'auditLog.filter': 'Filter',
  'auditLog.activeCount': '{n} active',
  'auditLog.collapse': 'Collapse',
  'auditLog.expand': 'Open',
  'auditLog.actionLabel': 'Action',
  'auditLog.actionPlaceholderLoading': 'loading…',
  'auditLog.actionPlaceholder': 'select action',
  'auditLog.actorEmailLabel': 'Actor email (substring)',
  'auditLog.actorEmailPlaceholder': '@transpay.com',
  'auditLog.actorScopeLabel': 'Actor scope',
  'auditLog.actorScopePlaceholder': 'all',
  'auditLog.actorScopeInternal': 'internal',
  'auditLog.actorScopeExternal': 'external (merchant)',
  'auditLog.actorScopeSystem': 'system',
  'auditLog.targetLabel': 'Target (substring)',
  'auditLog.targetPlaceholder': 'merchant:<uuid> / disbursement:<uuid> / etc.',
  'auditLog.dateLabel': 'Date (GMT+7)',
  'auditLog.datePlaceholder': 'pick a date range',
  'auditLog.reset': 'Reset',
  'auditLog.apply': 'Apply',
  'auditLog.loadFailed': 'Failed to load',
  'auditLog.colTime': 'Time (GMT+7)',
  'auditLog.colActivity': 'Activity',
  'auditLog.colActor': 'By',
  'auditLog.colType': 'Type',
  'auditLog.empty': 'No audit rows match the filter.',
  'auditLog.ipUndetected': 'IP not detected',
  'auditLog.pageRows': 'Page {page} · {count} rows',
  'auditLog.previous': 'Previous',
  'auditLog.next': 'Next',
  'auditLog.detailActivity': 'Activity Details',
  'auditLog.previousValue': 'Previous Value',
  'auditLog.target': 'Object',
  'auditLog.auditId': 'Audit ID',
  'auditLog.requestId': 'Request ID',
  'auditLog.hideRawJson': 'Hide raw JSON (for IT)',
  'auditLog.showRawJson': 'Show raw JSON (for IT)',
  'auditLog.rawBefore': 'before',
  'auditLog.rawAfter': 'after',

  // ===== apiDocs =====
  'apiDocs.reauthIntro':
    'The API Documentation page lists TransPay internal endpoints. To open it, please confirm your account password once more.',
  'apiDocs.accessDenied':
    'You do not have permission to open the API Documentation page. Contact your admin to request access.',
  'apiDocs.title': 'API Documentation',
  'apiDocs.subtitle':
    'List of endpoints you can call. Tailored to the role + permissions of the account you are signed in with — endpoints not meant for you are hidden.',
  'apiDocs.sessionInactive': 'Session not active yet. Sign in first to view the API docs.',
  'apiDocs.youAre': 'You are',
  'apiDocs.role': 'Role',
  'apiDocs.noRole': '(no role)',
  'apiDocs.visibleEndpoints': 'Visible endpoints',
  'apiDocs.visibleOf': '{visible} of {total}',
  'apiDocs.hiddenByPermissions': '{n} hidden by permissions',
  'apiDocs.permissionsHeld': 'Permissions held',
  'apiDocs.allSuperuserBypass': 'ALL (superuser bypass)',
  'apiDocs.searchLabel': 'Search',
  'apiDocs.searchPlaceholder': 'path, title, description, or permission key',
  'apiDocs.categoryLabel': 'Category',
  'apiDocs.categoryPlaceholder': 'all categories',
  'apiDocs.tabAll': 'All',
  'apiDocs.tabServerToServer': 'Server-to-Server',
  'apiDocs.tabDashboard': 'Dashboard',
  'apiDocs.noMatch': 'No endpoints match the current filters.',
  'apiDocs.badgeServerToServer': 'server-to-server',
  'apiDocs.badgeInternal': 'internal',
  'apiDocs.badgeMerchant': 'merchant',
  'apiDocs.baseUrl': 'Base URL',
  'apiDocs.baseUrlNote': 'Production. For other environments, swap the host.',
  'apiDocs.permissionRequired': 'Permission required (ANY of)',
  'apiDocs.requestHeaders': 'Request headers',
  'apiDocs.colName': 'Name',
  'apiDocs.paymentMethods': 'Payment Methods',
  'apiDocs.colCategory': 'Category',
  'apiDocs.colRequired': 'Required?',
  'apiDocs.colDescription': 'Description',
  'apiDocs.colExample': 'Example',
  'apiDocs.required': 'required',
  'apiDocs.optional': 'optional',
  'apiDocs.curlExample': 'curl example',
  'apiDocs.requestBody': 'Request body',
  'apiDocs.responseSample': 'Response (sample)',
  'apiDocs.responseFields': 'Response fields',
  'apiDocs.colPath': 'Path',
  'apiDocs.colType': 'Type',
  'apiDocs.responseHeaders': 'Response headers',
  'apiDocs.errorCodes': 'Error codes',
  'apiDocs.colCode': 'Code',
  'apiDocs.colHttp': 'HTTP',
  'apiDocs.fullPath': 'Full path:',

  // ===== merchantCreate =====
  'merchantCreate.internalOnly': 'Only internal users can create merchants.',
  'merchantCreate.title': 'Create Merchant',
  'merchantCreate.create': 'Create Merchant',
  'merchantCreate.basics': 'Basics',
  'merchantCreate.nameLabel': 'Name',
  'merchantCreate.emailLabel': 'Email',
  'merchantCreate.depositFeeLabel': 'Deposit fee (bps)',
  'merchantCreate.depositFeeDesc': '70 = 0.70 %',
  'merchantCreate.disbursementFeeLabel': 'Disbursement fee (IDR)',
  'merchantCreate.apiAccessLabel': 'API access enabled',
  'merchantCreate.valRequired': 'Required',
  'merchantCreate.valInvalidEmail': 'Invalid email',
  'merchantCreate.webhookTitle': 'Webhook (Superuser only)',
  'merchantCreate.depositWebhookLabel': 'Deposit webhook URL',
  'merchantCreate.depositWebhookDesc':
    'Where TransPay sends payment notifications. Superuser only.',
  'merchantCreate.payokTitle': 'PAYOK config (Superuser only)',
  'merchantCreate.payokEnable': 'Enable',
  'merchantCreate.payokManagedNote': 'PAYOK connection config (base URL, MID, endpoints, keys, notification URLs) is managed centrally in the Acquirer menu. When creating a merchant you only toggle payments on/off here.',
  'merchantCreate.cancel': 'Cancel',
  'merchantCreate.revealTitle': 'Merchant created — save these credentials',
  'merchantCreate.revealWarning':
    'The plaintext token + webhook secret are shown ONCE. Save them now before closing the dialog. After closing, only the 8-char suffix is visible.',
  'merchantCreate.fieldClientId': 'Transpay Client ID',
  'merchantCreate.fieldClientToken': 'Transpay Client Token',
  'merchantCreate.fieldWebhookSecret': 'Webhook HMAC Secret',
  'merchantCreate.revealFooterPre':
    'After clicking OK, you will be taken to the merchant detail page. Add at least 1 CIDR to',
  'merchantCreate.revealFooterPost':
    'before the cashin API can be used (deny-all default).',

  // ===== acceptInvite =====
  'acceptInvite.title': 'Accept Invitation',
  'acceptInvite.tokenMissing':
    'token missing — make sure you opened the full invite link',
  'acceptInvite.invalidOrExpired': 'invite invalid or expired',
  'acceptInvite.invitedAs': "You've been invited as:",
  'acceptInvite.noRole': 'No role assigned',
  'acceptInvite.invitedByExpires': 'Invited by {invitedBy} · expires {expiresAt}',
  'acceptInvite.emailLabel': 'Email',
  'acceptInvite.nameLabel': 'Your name',
  'acceptInvite.nameDesc': "How you'd like to appear in the dashboard",
  'acceptInvite.passwordLabel': 'Password',
  'acceptInvite.passwordDesc': 'min 12 characters',
  'acceptInvite.confirmLabel': 'Confirm password',
  'acceptInvite.submit': 'Accept & sign in',
  'acceptInvite.haveAccount': 'Already have an account?',
  'acceptInvite.signIn': 'Sign in',
  'acceptInvite.valRequired': 'required',
  'acceptInvite.valMinChars': 'min 12 chars',
  'acceptInvite.valMismatch': "doesn't match password",

  // ===== security =====
  'security.title': 'Security',
  'security.subtitle':
    'Your account security settings. MFA must be enabled before you can make disbursements.',
  'security.statusLoadFailed': 'Failed to load MFA status',
  'security.mfaTitle': 'Multi-Factor Authentication (TOTP)',
  'security.badgeActive': 'Active',
  'security.badgeNotEnabled': 'Not enabled',
  'security.mfaHint':
    'Use an authenticator app (Google Authenticator, Authy, 1Password, Bitwarden, etc.)',
  'security.disableMfa': 'Disable MFA',
  'security.enrollAuthenticator': 'Enroll authenticator',
  'security.labelEnrolled': 'Enrolled',
  'security.labelLastUsed': 'Last used',
  'security.labelBackupCodesLeft': 'Backup codes left',
  'security.backupCodesLowWarning':
    'Backup codes are running low. If you lose your authenticator device, you will have to ask an admin to reset MFA. Consider disabling + re-enrolling to get a fresh set of backup codes.',
  'security.enrollErrInit': 'Enroll init failed',
  'security.modalTitleSaveCodes': 'Save your backup codes',
  'security.modalTitleEnroll': 'Enroll authenticator app',
  'security.generatingSecret': 'Generating secret…',
  'security.enrollStep1':
    '1. Open your authenticator app (Google Authenticator, Authy, 1Password, Bitwarden, etc.). Scan the QR below <strong>or</strong> type the secret manually.',
  'security.qrAlt': 'MFA QR code',
  'security.secretManual': 'Manual secret:',
  'security.enrollStep2':
    '2. Once the secret is saved in the app, enter the 6-digit code it shows:',
  'security.verifyActivate': 'Verify & enable',
  'security.saveCodesTitle': 'Save these 10 backup codes NOW.',
  'security.saveCodesBody':
    'Each code can be used ONCE to submit a disbursement if you lose your authenticator device. They will not be shown again. Save them in a password manager, or print them and keep them somewhere safe.',
  'security.copiedAll10': 'Copied all 10',
  'security.copyAll': 'Copy all',
  'security.savedClose': "I've saved them, close",
  'security.retry': 'Try again',
  'security.disableMfaTitle': 'Disable MFA',
  'security.disableWarningPre': 'Once MFA is disabled, you',
  'security.disableWarningBold': 'cannot make disbursements',
  'security.disableWarningPost': 'until MFA is enabled again. Make sure this is what you want.',
  'security.accountPasswordLabel': 'Account password',
  'security.mfaCodeLabel': 'MFA code (from authenticator)',
};

export const translations = { id, en } as const;
export type TranslationKey = keyof typeof id;

/**
 * Non-React translation lookup for code OUTSIDE the component tree — e.g. the
 * global React Query MutationCache toast handler in main.tsx. Reads the
 * persisted locale directly; mirrors the t() fallback + {var} interpolation.
 */
export function translateStatic(
  key: TranslationKey,
  vars?: Record<string, string | number>,
): string {
  let locale: keyof typeof translations = 'id';
  try {
    const s = typeof localStorage !== 'undefined' ? localStorage.getItem('transpay_locale') : null;
    if (s === 'en' || s === 'id') locale = s;
  } catch {
    /* private mode / SSR — fall back to id */
  }
  let str: string = translations[locale][key] ?? translations.id[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return str;
}
